import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface TranslationKeys {
  // Navigation
  languages: string;
  languageSettings: string;
  voiceType: string;
  account: string;
  profile: string;
  settings: string;
  history: string;
  wordUsage: string;
  imageUsage: string;
  analytics: string;
  buyWords: string;
  buyImages: string;
  buyDataFromWebapp: string;
  login: string;
  register: string;
  logout: string;

  // Buy Modals
  buyWordsTitle: string;
  buyWordsSubtitle: string;
  buyImagesTitle: string;
  buyImagesSubtitle: string;
  conversationTopicPlaceholder: string;
  conversationTopicHint: string;
  selectWordPackage: string;
  selectImagePackage: string;
  oneTimePurchase: string;
  buyMoreSaveMore: string;
  popular: string;
  data: string;
  wordCredits: string;
  imageProcessingCredits: string;
  perfectForTesting: string;
  save: string;
  selectAnOption: string;
  howItWorks: string;
  oneTimePurchaseAddsWords: string;
  wordsConsumedAsYouUse: string;
  noExpirationUseWords: string;
  oneTimePurchaseAddsImages: string;
  imagesConsumedWhenYouUseOCR: string;
  noExpirationUseImages: string;

  // Language Selection
  yourLanguage: string;
  partnerLanguage: string;
  speakingSpeed: string;
  slow: string;
  fast: string;

  // Main App
  translation: string;
  start: string;
  end: string;
  clear: string;
  clearHistory: string;
  showAll: string;
  readImage: string;
  translate: string;
  typeMessage: string;
  typePartnerMessage: string;
  maxWords: string;
  words: string;
  characters: string;

  // Conversation
  youSpeakLabel: string;
  partnerSpeaksLabel: string;

  // History Modal
  conversationHistory: string;
  searchConversations: string;
  allAudioFiles: string;
  clearAll: string;
  downloadAll: string;
  play: string;
  delete: string;
  confidence: string;
  recorded: string;
  conversations: string;
  totalDuration: string;
  noConversationsFound: string;
  noConversationsYet: string;
  tryAdjustingSearch: string;
  startConversationToSee: string;
  youWrote: string;
  youSpoke: string;
  partnerWrote: string;
  partnerHears: string;
  noOriginalText: string;
  noTranslation: string;
  download: string;
  generateAudioFor: string;
  generating: string;

  // Word Usage Modal
  wordUsageAndQuotas: string;
  trackTranslationUsage: string;
  currentUsage: string;
  wordsUsage: string;
  switchToTotalView: string;
  switchToRemainingView: string;
  of: string;
  lastReset: string;
  quotaSettings: string;
  enableWordQuotas: string;
  setLimitsOnWordUsage: string;
  wordLimit: string;
  maximumWordsPerResetPeriod: string;
  resetPeriod: string;
  daily: string;
  weekly: string;
  monthly: string;
  neverManualResetOnly: string;
  howOftenUsageStatsReset: string;

  // Image Usage Modal
  trackImageProcessingUsage: string;
  imagesUsage: string;
  nearLimit: string;
  warning: string;
  good: string;
  imagesUsed: string;
  remaining: string;
  imagesRemaining: string;
  imagesUsedText: string;
  imagesAvailable: string;
  statistics: string;
  totalImagesProcessed: string;
  lastActive: string;
  never: string;
  accountCreated: string;
  imageLimitReached: string;
  imageLimitReachedMessage: string;
  loadingImageUsageData: string;

  // Status Messages
  hello: string;
  processing: string;
  recording: string;
  translating: string;
  error: string;
  success: string;

  // Success Messages
  wordsAddedSuccessfully: string;
  imagesAddedSuccessfully: string;
  textExtractedSuccessfully: string;
  audioGeneratedSuccessfully: string;
  originalAudioGeneratedSuccessfully: string;
  usageStatsResetSuccessfully: string;
  purchaseSuccessful: string;
  paymentSuccessful: string;

  // Error Messages
  wordLimitExceeded: string;
  wordLimitExceededTitle: string;
  wordLimitExceededMessage: string;
  wordLimitExceededDetail: string;
  textExceedsWordLimit: string;
  pleaseBuyMoreWords: string;
  pleaseBuyMoreWordsToContinue: string;
  purchaseFailed: string;
  purchaseFailedTryAgain: string;
  readImageFailed: string;
  noTextFoundInImage: string;
  translationFailed: string;
  translationFailedTryAgain: string;
  wordsRemainingWarning: string;
  approachingWordLimit: string;

  // Continuous Conversation Modal
  continuousConversation: string;
  bidirectionalConversation: string;
  readyToTranscribe: string;
  transcribing: string;
  startTranscribing: string;
  stopTranscribing: string;
  transcripts: string;
  conversation: string;
  noTranscriptsYet: string;
  buyMoreWords: string;
  dismiss: string;
  lectureMode: string;
  lectureModeDescription: string;
  advancedModel: string;
  advancedModelDescription: string;
  liveTranslation: string;
  liveTranslationDescription: string;
  liveTranslationFallback: string;
  liveTranslationNotSupported: string;

  // Broadcast Translation Modal
  broadcastTranslation: string;
  speaker: string;
  listener: string;
  sourceLanguage: string;
  sourceLanguageSet: string;
  targetLanguage: string;
  addLanguage: string;
  broadcastCode: string;
  shareCode: string;
  startBroadcast: string;
  stopBroadcast: string;
  enterCode: string;
  selectLanguage: string;
  joinBroadcast: string;
  listeningTo: string;
  leaveBroadcast: string;
  messages: string;
  waitingForMessages: string;
  availableLanguages: string;
  conversationTopic: string;
  topicPlaceholder: string;
  topicHint: string;
  audioOn: string;
  audioOff: string;
  speaking: string;
  wordCountWarning: string;

  // Auth0 Error Messages
  auth0LoginFailed: string;
  auth0LoginTimedOut: string;
  auth0RegistrationFailed: string;
  auth0RegistrationTimedOut: string;
  invalidAuthResponse: string;
  facebookLoginDenied: string;
  facebookPermissionsRequired: string;
      loginRequired: string;
      notMobilePlatform: string;
      transcriptionTimeout: string;
      transcriptionTimeoutTitle: string;
      transcriptionTimeoutMessage: string;
      transcriptionFailed: string;
      retryingTranscription: string;

  // Support Page
  supportAndFeedback: string;
  supportHeaderSubtitle: string;
  supportThankYou: string;
  supportFeedbackInvaluable: string;
  supportImproveServices: string;
  supportMultilingualCommunication: string;
  supportQuestionsSuggestions: string;
  supportTechnicalSupport: string;
  supportShareExperience: string;
  supportLoveToHear: string;
  supportGetInTouch: string;
  supportContactUs: string;
  supportSendEmail: string;
  supportCopyEmailAddress: string;
  supportEmailCopied: string;
  supportBackToApp: string;
  supportDeleteAccount: string;
  supportDeleteAccountDescription: string;
  supportRequestAccountDeletion: string;
  supportDeleteAccountConfirmation: string;
  supportDeleteAccountFinalConfirm: string;
  supportAccountDeleted: string;
  supportDeleteAccountError: string;
  supportDeletingAccount: string;
  support: string;
}

@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  private currentLanguage = new BehaviorSubject<string>('en');
  public currentLanguage$ = this.currentLanguage.asObservable();

  constructor() {
    // Initialize with English by default
    // The app component will update this when it loads the user's preference
    console.log('🔄 TranslationService: Initialized with default language: en');
  }

  private translations: { [key: string]: TranslationKeys } = {
    en: {
      // Navigation
      languages: 'Languages',
      languageSettings: 'Language Settings',
      voiceType: 'Voice Type',
      account: 'Account',
      profile: 'Profile',
      settings: 'Settings',
      history: 'History',
      wordUsage: 'Word Usage',
      imageUsage: 'Image Usage',
      analytics: 'Analytics',
      buyWords: 'Buy Translation Data',
      buyImages: 'Buy Image Data',
      buyDataFromWebapp: 'Buy Data From Webapp',
      login: 'Login',
      register: 'Register',
      logout: 'Logout',

      // Buy Modals
      buyWordsTitle: 'Buy Words',
      buyWordsSubtitle: 'Purchase additional data for translation',
      buyImagesTitle: 'Buy Images',
      buyImagesSubtitle: 'Purchase additional image processing credits',
      conversationTopicPlaceholder: 'Conversation topic (e.g., dental lecture, medical conference)',
      conversationTopicHint: 'Optional: Helps improve translation accuracy for specific topics',
      selectWordPackage: 'Select Word Package',
      selectImagePackage: 'Select Image Package',
      oneTimePurchase: 'One-time purchase',
      buyMoreSaveMore: 'Buy more, save more!',
      popular: 'Popular',
      data: 'data',
      wordCredits: 'word credits',
      imageProcessingCredits: 'image processing credits',
      perfectForTesting: 'Perfect for testing',
      save: 'Save',
      selectAnOption: 'Select an Option',
      howItWorks: 'How it works:',
      oneTimePurchaseAddsWords:
        'One-time purchase adds words to your account permanently',
      wordsConsumedAsYouUse:
        'Words are consumed as you use translation features',
      noExpirationUseWords: 'No expiration - use words whenever you need them',
      oneTimePurchaseAddsImages:
        'One-time purchase adds images to your account permanently',
      imagesConsumedWhenYouUseOCR:
        'Images are consumed when you use text extraction',
      noExpirationUseImages:
        'No expiration - use images whenever you need them',

      // Language Selection
      yourLanguage: 'Your Language:',
      partnerLanguage: 'Partner Language:',
      speakingSpeed: 'Speaking Speed:',
      slow: 'Slow',
      fast: 'Fast',

      // Main App
      translation: 'Translation',
      start: 'Start',
      end: 'End',
      clear: 'Clear',
      clearHistory: 'Clear History',
      showAll: 'Show All',
      readImage: 'Read Image',
      translate: 'Translate',
      typeMessage: 'Type your message here...',
      typePartnerMessage: "Type partner's message here...",
      maxWords: 'max 200 words',
      words: 'words',
      characters: 'characters',

      // Conversation
      youSpeakLabel: 'You speak',
      partnerSpeaksLabel: 'Partner speaks',

      // History Modal
      conversationHistory: 'Conversation History',
      searchConversations: 'Search conversations...',
      allAudioFiles: 'All Audio Files',
      clearAll: 'Clear All',
      downloadAll: 'Download All',
      play: 'Play',
      delete: 'Delete',
      confidence: 'Confidence',
      recorded: 'Recorded',
      conversations: 'conversations',
      totalDuration: 'total duration',
      noConversationsFound: 'No conversations found',
      noConversationsYet: 'No conversations yet',
      tryAdjustingSearch: 'Try adjusting your search terms',
      startConversationToSee: 'Start a conversation to see it here',
      youWrote: 'You wrote',
      youSpoke: 'You spoke',
      partnerWrote: 'Partner wrote',
      partnerHears: 'Partner hears',
      noOriginalText: 'No original text',
      noTranslation: 'No translation',
      download: 'Download',
      generateAudioFor: 'Generate Audio for',
      generating: 'Generating...',

      // Word Usage Modal
      wordUsageAndQuotas: 'Word Usage & Quotas',
      trackTranslationUsage: 'Track your translation usage and manage quotas',
      currentUsage: 'Current Usage',
      wordsUsage: 'Words Usage',
      switchToTotalView: 'Switch to Total view',
      switchToRemainingView: 'Switch to Remaining view',
      of: 'of',
      lastReset: 'Last reset:',
      quotaSettings: 'Quota Settings',
      enableWordQuotas: 'Enable Word Quotas',
      setLimitsOnWordUsage: 'Set limits on word usage',
      wordLimit: 'Word Limit',
      maximumWordsPerResetPeriod: 'Maximum words per reset period',
      resetPeriod: 'Reset Period',
      daily: 'Daily',
      weekly: 'Weekly',
      monthly: 'Monthly',
      neverManualResetOnly: 'Never (Manual reset only)',
      howOftenUsageStatsReset: 'How often usage statistics are reset',

      // Image Usage Modal
      trackImageProcessingUsage: 'Track your image processing usage',
      imagesUsage: 'Images Usage',
      nearLimit: 'Near Limit',
      warning: 'Warning',
      good: 'Good',
      imagesUsed: 'Images Used',
      remaining: 'Remaining',
      imagesRemaining: 'images remaining',
      imagesUsedText: 'images used',
      imagesAvailable: 'images available',
      statistics: 'Statistics',
      totalImagesProcessed: 'Total Images Processed:',
      lastActive: 'Last Active:',
      never: 'Never',
      accountCreated: 'Account Created:',
      imageLimitReached: 'Image Limit Reached',
      imageLimitReachedMessage: 'You have reached your image processing limit. Purchase more images to continue.',
      loadingImageUsageData: 'Loading image usage data...',

      // Status Messages
      hello: 'Hello',
      processing: 'Processing...',
      recording: 'Recording...',
      translating: 'Translating...',
      error: 'Error',
      success: 'Success',

      // Success Messages
      wordsAddedSuccessfully: 'Successfully added {0} words to your account!',
      imagesAddedSuccessfully: 'Successfully added {0} images to your account!',
      textExtractedSuccessfully: 'Text extracted from image successfully!',
      audioGeneratedSuccessfully: 'Audio generated successfully!',
      originalAudioGeneratedSuccessfully: 'Original audio generated successfully!',
      usageStatsResetSuccessfully: 'Usage statistics have been reset successfully.',
      purchaseSuccessful: 'Purchase successful!',
      paymentSuccessful: 'Payment successful!',

      // Error Messages
      wordLimitExceeded: 'Word limit exceeded',
      wordLimitExceededTitle: 'Word Limit Exceeded',
      wordLimitExceededMessage: 'Word limit exceeded. Please buy more words to continue.',
      wordLimitExceededDetail: "You've used {0} out of {1} words. Please buy more words to continue.",
      textExceedsWordLimit: 'Text exceeds 200 words limit. Please shorten your message.',
      pleaseBuyMoreWords: 'Please buy more words',
      pleaseBuyMoreWordsToContinue: 'Please buy more words to continue.',
      purchaseFailed: 'Failed to purchase',
      purchaseFailedTryAgain: 'Failed to purchase. Please try again.',
      readImageFailed: 'Failed to read text from image',
      noTextFoundInImage: 'No text found in the image',
      translationFailed: 'Translation failed',
      translationFailedTryAgain: 'Translation failed. Please try again.',
      wordsRemainingWarning: 'Warning: You have only {0} words remaining ({1}% used). Consider buying more words soon.',
      approachingWordLimit: 'Approaching word limit',

      // Continuous Conversation Modal
      continuousConversation: 'Continuous Conversation',
      bidirectionalConversation: 'Bidirectional Conversation',
      readyToTranscribe: 'Ready to transcribe',
      transcribing: 'Transcribing...',
      startTranscribing: 'Start Transcribing',
      stopTranscribing: 'Stop Transcribing',
      transcripts: 'Transcripts',
      conversation: 'Conversation',
      noTranscriptsYet: 'No transcripts yet. Start transcribing to begin!',
      buyMoreWords: 'Buy More Words',
      dismiss: 'Dismiss',
      lectureMode: 'Lecture Mode',
      lectureModeDescription: 'Record every 20 seconds for better translation context',
      advancedModel: 'Advanced translation model',
      advancedModelDescription: 'Uses gpt-5.4-mini. May be slower.',
      liveTranslation: "Live translation",
      liveTranslationDescription: "Translates while you speak (gpt-realtime-translate) in Continuous Conversation and Broadcast. Speech in: English, Korean, Mandarin, Russian, Kazakh, Uzbek, Farsi, Spanish. Live voice out: English, Korean, Mandarin, Russian, Spanish.",
      liveTranslationFallback: "Live translation doesn't support {0} → {1}. Using standard translation instead.",
      liveTranslationNotSupported: "not available in live translation",

      // Broadcast Translation Modal
      broadcastTranslation: 'Broadcast Translation',
      speaker: 'Speaker',
      listener: 'Listener',
      sourceLanguage: 'Source Language',
      sourceLanguageSet: 'Source language is set from your section',
      targetLanguage: 'Target Language',
      addLanguage: 'Add Language',
      broadcastCode: 'Broadcast Code',
      shareCode: 'Share this code with listeners',
      startBroadcast: 'Start Broadcast',
      stopBroadcast: 'Stop Broadcast',
      enterCode: 'Enter Broadcast Code',
      selectLanguage: 'Select Language',
      joinBroadcast: 'Join Broadcast',
      listeningTo: 'Listening to',
      leaveBroadcast: 'Leave Broadcast',
      messages: 'Messages',
      waitingForMessages: 'Waiting for messages...',
      availableLanguages: 'Available languages',
      conversationTopic: 'Conversation Topic (Optional)',
      topicPlaceholder: 'e.g., Medical consultation, Business meeting, Travel planning...',
      topicHint: 'Adding a topic helps improve translation accuracy',
        audioOn: 'Audio: ON',
        audioOff: 'Audio: OFF',
        speaking: 'Speaking...',
        wordCountWarning: 'Note: Each additional language will multiply word counts (e.g., 2 languages = 2x words, 3 languages = 3x words)',

      // Auth0 Error Messages
      auth0LoginFailed: 'Auth0 login failed',
      auth0LoginTimedOut: 'Auth0 login timed out',
      auth0RegistrationFailed: 'Auth0 registration failed',
      auth0RegistrationTimedOut: 'Auth0 registration timed out',
      invalidAuthResponse: 'Invalid auth response',
      facebookLoginDenied: 'Facebook login denied',
      facebookPermissionsRequired: 'Facebook permissions required',
      loginRequired: 'Login required',
      notMobilePlatform: 'This feature is only available on mobile platforms',
      transcriptionTimeout: 'Transcription timeout',
      transcriptionTimeoutTitle: 'Transcription Timeout',
      transcriptionTimeoutMessage: 'The transcription request took too long. Retrying...',
      transcriptionFailed: 'Transcription failed. Please try again.',
      retryingTranscription: 'Retrying transcription...',

      // Support Page
      supportAndFeedback: 'Support & Feedback',
      supportHeaderSubtitle: "We're here to help and would love to hear from you",
      supportThankYou: 'Thank you for using',
      supportFeedbackInvaluable: 'Your feedback is invaluable to us',
      supportImproveServices: 'as we continue to improve our translation services',
      supportMultilingualCommunication: 'and make multilingual communication',
      supportQuestionsSuggestions: 'Whether you have questions, suggestions,',
      supportTechnicalSupport: 'need technical support,',
      supportShareExperience: 'or want to share your experience with TilHub,',
      supportLoveToHear: "we'd love to hear from you.",
      supportGetInTouch: 'Get in Touch',
      supportContactUs: 'For feedback, questions, or support, please contact us at:',
      supportSendEmail: 'Send Email',
      supportCopyEmailAddress: 'Copy Email Address',
      supportEmailCopied: 'Email address copied to clipboard!',
      supportBackToApp: 'Back to App',
      supportDeleteAccount: 'Delete Your Account',
      supportDeleteAccountDescription: 'You can request deletion of your account and all associated data at any time.',
      supportRequestAccountDeletion: 'Request Account Deletion',
      supportDeleteAccountConfirmation: 'Are you sure you want to delete your account? This action cannot be undone. All your data will be permanently deleted.',
      supportDeleteAccountFinalConfirm: 'This is your final warning. Your account and all data will be permanently deleted. Are you absolutely sure?',
      supportAccountDeleted: 'Your account has been deleted successfully. You will be logged out now.',
      supportDeleteAccountError: 'Failed to delete account. Please try again or contact support.',
      supportDeletingAccount: 'Deleting...',
      support: 'Support',
    },

    ko: {
      // Navigation
      languages: '언어',
      languageSettings: '언어 설정',
      voiceType: '음성 유형',
      account: '계정',
      profile: '프로필',
      settings: '설정',
      history: '기록',
      wordUsage: '단어 사용량',
      imageUsage: '이미지 사용량',
      analytics: '분석',
      buyWords: '번역 데이터 구매',
      buyImages: '이미지 데이터 구매',
      buyDataFromWebapp: '웹앱에서 데이터 구매',
      login: '로그인',
      register: '회원가입',
      logout: '로그아웃',

      // Buy Modals
      buyWordsTitle: '단어 구매',
      buyWordsSubtitle: '번역을 위한 추가 데이터 구매',
      buyImagesTitle: '이미지 구매',
      buyImagesSubtitle: '추가 이미지 처리 크레딧 구매',
      conversationTopicPlaceholder: '대화 주제 (예: 치과 강의, 의학 회의)',
      conversationTopicHint: '선택사항: 특정 주제에 대한 번역 정확도 향상에 도움',
      selectWordPackage: '단어 패키지 선택',
      selectImagePackage: '이미지 패키지 선택',
      oneTimePurchase: '일회성 구매',
      buyMoreSaveMore: '더 많이 사면 더 많이 절약!',
      popular: '인기',
      data: '데이터',
      wordCredits: '단어 크레딧',
      imageProcessingCredits: '이미지 처리 크레딧',
      perfectForTesting: '테스트에 완벽',
      save: '절약',
      selectAnOption: '옵션 선택',
      howItWorks: '작동 방식:',
      oneTimePurchaseAddsWords:
        '일회성 구매로 계정에 단어가 영구적으로 추가됩니다',
      wordsConsumedAsYouUse: '번역 기능을 사용할 때마다 단어가 소모됩니다',
      noExpirationUseWords: '만료 없음 - 필요할 때마다 단어를 사용하세요',
      oneTimePurchaseAddsImages:
        '일회성 구매로 계정에 이미지가 영구적으로 추가됩니다',
      imagesConsumedWhenYouUseOCR:
        '텍스트 추출을 사용할 때마다 이미지가 소모됩니다',
      noExpirationUseImages: '만료 없음 - 필요할 때마다 이미지를 사용하세요',

      // Language Selection
      yourLanguage: '당신의 언어:',
      partnerLanguage: '상대방의 언어:',
      speakingSpeed: '말하기 속도:',
      slow: '느리게',
      fast: '빠르게',

      // Main App
      translation: '번역',
      start: '시작',
      end: '종료',
      clear: '지우기',
      clearHistory: '기록 지우기',
      showAll: '모두 보기',
      readImage: '이미지 읽기',
      translate: '번역',
      typeMessage: '여기에 메시지를 입력하세요...',
      typePartnerMessage: '상대방의 메시지를 여기에 입력하세요...',
      maxWords: '최대 200단어',
      words: '단어',
      characters: '글자',

      // Conversation
      youSpeakLabel: '당신이 말함',
      partnerSpeaksLabel: '상대방이 말함',

      // History Modal
      conversationHistory: '대화 기록',
      searchConversations: '대화 검색...',
      allAudioFiles: '모든 오디오 파일',
      clearAll: '모두 지우기',
      downloadAll: '모두 다운로드',
      play: '재생',
      delete: '삭제',
      confidence: '신뢰도',
      recorded: '녹음됨',
      conversations: '대화',
      totalDuration: '총 시간',
      noConversationsFound: '대화를 찾을 수 없음',
      noConversationsYet: '아직 대화가 없습니다',
      tryAdjustingSearch: '검색어를 조정해 보세요',
      startConversationToSee: '대화를 시작하면 여기에 표시됩니다',
      youWrote: '당신이 작성',
      youSpoke: '당신이 말함',
      partnerWrote: '상대방이 작성',
      partnerHears: '상대방이 듣음',
      noOriginalText: '원문 없음',
      noTranslation: '번역 없음',
      download: '다운로드',
      generateAudioFor: '오디오 생성 ',
      generating: '생성 중...',

      // Word Usage Modal
      wordUsageAndQuotas: '단어 사용량 및 할당량',
      trackTranslationUsage: '번역 사용량을 추적하고 할당량을 관리하세요',
      currentUsage: '현재 사용량',
      wordsUsage: '단어 사용량',
      switchToTotalView: '전체 보기로 전환',
      switchToRemainingView: '남은 양 보기로 전환',
      of: '/',
      lastReset: '마지막 초기화:',
      quotaSettings: '할당량 설정',
      enableWordQuotas: '단어 할당량 활성화',
      setLimitsOnWordUsage: '단어 사용에 제한 설정',
      wordLimit: '단어 한도',
      maximumWordsPerResetPeriod: '재설정 기간당 최대 단어 수',
      resetPeriod: '재설정 기간',
      daily: '매일',
      weekly: '매주',
      monthly: '매월',
      neverManualResetOnly: '안 함 (수동 재설정만)',
      howOftenUsageStatsReset: '사용 통계가 재설정되는 빈도',

      // Image Usage Modal
      trackImageProcessingUsage: '이미지 처리 사용량 추적',
      imagesUsage: '이미지 사용량',
      nearLimit: '한도 근접',
      warning: '경고',
      good: '양호',
      imagesUsed: '사용된 이미지',
      remaining: '남음',
      imagesRemaining: '개 이미지 남음',
      imagesUsedText: '개 이미지 사용됨',
      imagesAvailable: '개 이미지 사용 가능',
      statistics: '통계',
      totalImagesProcessed: '처리된 총 이미지:',
      lastActive: '마지막 활동:',
      never: '없음',
      accountCreated: '계정 생성일:',
      imageLimitReached: '이미지 한도 도달',
      imageLimitReachedMessage: '이미지 처리 한도에 도달했습니다. 계속하려면 더 많은 이미지를 구매하세요.',
      loadingImageUsageData: '이미지 사용량 데이터 로딩 중...',

      // Status Messages
      hello: '안녕하세요',
      processing: '처리 중...',
      recording: '녹음 중...',
      translating: '번역 중...',
      error: '오류',
      success: '성공',

      // Success Messages
      wordsAddedSuccessfully: '계정에 {0}개의 단어가 성공적으로 추가되었습니다!',
      imagesAddedSuccessfully: '계정에 {0}개의 이미지가 성공적으로 추가되었습니다!',
      textExtractedSuccessfully: '이미지에서 텍스트를 성공적으로 추출했습니다!',
      audioGeneratedSuccessfully: '오디오가 성공적으로 생성되었습니다!',
      originalAudioGeneratedSuccessfully: '원본 오디오가 성공적으로 생성되었습니다!',
      usageStatsResetSuccessfully: '사용 통계가 성공적으로 재설정되었습니다.',
      purchaseSuccessful: '구매 성공!',
      paymentSuccessful: '결제 성공!',

      // Error Messages
      wordLimitExceeded: '단어 한도 초과',
      wordLimitExceededTitle: '단어 한도 초과',
      wordLimitExceededMessage: '단어 한도가 초과되었습니다. 계속하려면 더 많은 단어를 구매하세요.',
      wordLimitExceededDetail: '단어 한도 초과! {0}개 중 {1}개의 단어를 사용했습니다. 계속하려면 더 많은 단어를 구매하세요.',
      textExceedsWordLimit: '텍스트가 200단어 제한을 초과합니다. 메시지를 짧게 해주세요.',
      pleaseBuyMoreWords: '더 많은 단어 구매',
      pleaseBuyMoreWordsToContinue: '계속하려면 더 많은 단어를 구매하세요.',
      purchaseFailed: '구매 실패',
      purchaseFailedTryAgain: '구매에 실패했습니다. 다시 시도해주세요.',
      readImageFailed: '이미지에서 텍스트를 읽지 못했습니다',
      noTextFoundInImage: '이미지에서 텍스트를 찾을 수 없습니다',
      translationFailed: '번역 실패',
      translationFailedTryAgain: '번역에 실패했습니다. 다시 시도해주세요.',
      wordsRemainingWarning: '경고: {0}개의 단어만 남았습니다 ({1}% 사용됨). 곧 더 많은 단어를 구매하는 것을 고려하세요.',
      approachingWordLimit: '단어 한도에 근접',

      // Continuous Conversation Modal
      continuousConversation: '연속 대화',
      bidirectionalConversation: '양방향 대화',
      readyToTranscribe: '동시 통역 준비됨',
      transcribing: '동시 통역 중...',
      startTranscribing: '동시 통역 시작',
      stopTranscribing: '동시 통역 중지',
      transcripts: '동시 통역본',
      conversation: '대화',
      noTranscriptsYet: '아직 동시 통역본이 없습니다. 동시 통역을 시작하세요!',
      buyMoreWords: '더 많은 단어 구매',
      dismiss: '닫기',
      lectureMode: '강의 모드',
      lectureModeDescription: '더 나은 번역 컨텍스트를 위해 20초마다 녹음',
      advancedModel: '고급 번역 모델',
      advancedModelDescription: 'gpt-5.4-mini를 사용합니다. 더 느릴 수 있습니다.',
      liveTranslation: "실시간 통역",
      liveTranslationDescription: "말하는 동안 바로 번역합니다(gpt-realtime-translate). 연속 대화와 방송에서 사용됩니다. 입력 언어: 영어, 한국어, 중국어(표준어), 러시아어, 카자흐어, 우즈베크어, 페르시아어, 스페인어. 음성 출력: 영어, 한국어, 중국어(표준어), 러시아어, 스페인어.",
      liveTranslationFallback: "실시간 통역은 {0} → {1}을(를) 지원하지 않습니다. 일반 번역을 사용합니다.",
      liveTranslationNotSupported: "실시간 통역 미지원",

      // Broadcast Translation Modal
      broadcastTranslation: '방송 번역',
      speaker: '화자',
      listener: '청취자',
      sourceLanguage: '원본 언어',
      sourceLanguageSet: '원본 언어는 섹션에서 설정됩니다',
      targetLanguage: '대상 언어',
      addLanguage: '언어 추가',
      broadcastCode: '방송 코드',
      shareCode: '이 코드를 청취자와 공유하세요',
      startBroadcast: '방송 시작',
      stopBroadcast: '방송 중지',
      enterCode: '방송 코드 입력',
      selectLanguage: '언어 선택',
      joinBroadcast: '방송 참여',
      listeningTo: '청취 중',
      leaveBroadcast: '방송 나가기',
      messages: '메시지',
      waitingForMessages: '메시지 대기 중...',
      availableLanguages: '사용 가능한 언어',
      conversationTopic: '대화 주제 (선택사항)',
      topicPlaceholder: '예: 의료 상담, 비즈니스 회의, 여행 계획...',
      topicHint: '주제를 추가하면 번역 정확도가 향상됩니다',
      audioOn: '오디오: 켜짐',
      audioOff: '오디오: 꺼짐',
      speaking: '말하는 중...',
      wordCountWarning: '참고: 추가 언어마다 단어 수가 배수로 증가합니다 (예: 2개 언어 = 2배, 3개 언어 = 3배)',

      // Auth0 Error Messages
      auth0LoginFailed: 'Auth0 로그인 실패',
      auth0LoginTimedOut: 'Auth0 로그인 시간 초과',
      auth0RegistrationFailed: 'Auth0 등록 실패',
      auth0RegistrationTimedOut: 'Auth0 등록 시간 초과',
      invalidAuthResponse: '잘못된 인증 응답',
      facebookLoginDenied: 'Facebook 로그인 거부됨',
      facebookPermissionsRequired: 'Facebook 권한이 필요합니다',
      loginRequired: '로그인이 필요합니다',
      notMobilePlatform: '이 기능은 모바일 플랫폼에서만 사용할 수 있습니다',
      transcriptionTimeout: '전사 시간 초과',
      transcriptionTimeoutTitle: '전사 시간 초과',
      transcriptionTimeoutMessage: '전사 요청이 너무 오래 걸렸습니다. 재시도 중...',
      transcriptionFailed: '전사에 실패했습니다. 다시 시도해주세요.',
      retryingTranscription: '전사 재시도 중...',

      // Support Page
      supportAndFeedback: '지원 및 피드백',
      supportHeaderSubtitle: '저희는 도움을 드리기 위해 여기 있으며 여러분의 소식을 듣고 싶습니다',
      supportThankYou: '사용해 주셔서 감사합니다',
      supportFeedbackInvaluable: '귀하의 피드백은 저희에게 매우 소중합니다',
      supportImproveServices: '번역 서비스를 계속 개선하고',
      supportMultilingualCommunication: '다국어 소통을',
      supportQuestionsSuggestions: '질문이나 제안이 있으시거나,',
      supportTechnicalSupport: '기술 지원이 필요하시거나,',
      supportShareExperience: 'TilHub 사용 경험을 공유하고 싶으시다면,',
      supportLoveToHear: '저희는 여러분의 소식을 듣고 싶습니다.',
      supportGetInTouch: '연락하기',
      supportContactUs: '피드백, 질문 또는 지원을 위해 다음으로 연락해 주세요:',
      supportSendEmail: '이메일 보내기',
      supportCopyEmailAddress: '이메일 주소 복사',
      supportEmailCopied: '이메일 주소가 클립보드에 복사되었습니다!',
      supportBackToApp: '앱으로 돌아가기',
      supportDeleteAccount: '계정 삭제',
      supportDeleteAccountDescription: '언제든지 계정 및 관련 데이터 삭제를 요청할 수 있습니다.',
      supportRequestAccountDeletion: '계정 삭제 요청',
      supportDeleteAccountConfirmation: '계정을 삭제하시겠습니까? 이 작업은 취소할 수 없습니다. 모든 데이터가 영구적으로 삭제됩니다.',
      supportDeleteAccountFinalConfirm: '최종 경고입니다. 계정과 모든 데이터가 영구적으로 삭제됩니다. 정말 확실합니까?',
      supportAccountDeleted: '계정이 성공적으로 삭제되었습니다. 지금 로그아웃됩니다.',
      supportDeleteAccountError: '계정 삭제에 실패했습니다. 다시 시도하거나 지원팀에 문의하세요.',
      supportDeletingAccount: '삭제 중...',
      support: '지원',
    },

    zh: {
      // Navigation
      languages: '语言',
      languageSettings: '语言设置',
      voiceType: '语音类型',
      account: '账户',
      profile: '个人资料',
      settings: '设置',
      history: '历史记录',
      wordUsage: '单词使用量',
      imageUsage: '图片使用量',
      analytics: '分析',
      buyWords: '购买翻译数据',
      buyImages: '购买图像数据',
      buyDataFromWebapp: '从网页应用购买数据',
      login: '登录',
      register: '注册',
      logout: '退出',

      // Buy Modals
      buyWordsTitle: '购买单词',
      buyWordsSubtitle: '购买额外的翻译数据',
      buyImagesTitle: '购买图像',
      buyImagesSubtitle: '购买额外的图像处理积分',
      conversationTopicPlaceholder: '对话主题（例如：牙科讲座、医学会议）',
      conversationTopicHint: '可选：有助于提高特定主题的翻译准确性',
      selectWordPackage: '选择单词包',
      selectImagePackage: '选择图像包',
      oneTimePurchase: '一次性购买',
      buyMoreSaveMore: '买得越多，省得越多！',
      popular: '热门',
      data: '数据',
      wordCredits: '单词积分',
      imageProcessingCredits: '图像处理积分',
      perfectForTesting: '测试完美',
      save: '节省',
      selectAnOption: '选择选项',
      howItWorks: '工作原理：',
      oneTimePurchaseAddsWords: '一次性购买将单词永久添加到您的账户',
      wordsConsumedAsYouUse: '使用翻译功能时会消耗单词',
      noExpirationUseWords: '无过期 - 随时使用单词',
      oneTimePurchaseAddsImages: '一次性购买将图像永久添加到您的账户',
      imagesConsumedWhenYouUseOCR: '使用文本提取时会消耗图像',
      noExpirationUseImages: '无过期 - 随时使用图像',

      // Language Selection
      yourLanguage: '您的语言:',
      partnerLanguage: '对方的语言:',
      speakingSpeed: '语速:',
      slow: '慢',
      fast: '快',

      // Main App
      translation: '翻译',
      start: '开始',
      end: '结束',
      clear: '清除',
      clearHistory: '清除历史',
      showAll: '显示全部',
      readImage: '读取图片',
      translate: '翻译',
      typeMessage: '在此输入您的消息...',
      typePartnerMessage: '在此输入对方的消息...',
      maxWords: '最多200字',
      words: '字',
      characters: '字符',

      // Conversation
      youSpeakLabel: '您说',
      partnerSpeaksLabel: '对方说',

      // History Modal
      conversationHistory: '对话历史',
      searchConversations: '搜索对话...',
      allAudioFiles: '所有音频文件',
      clearAll: '全部清除',
      downloadAll: '全部下载',
      play: '播放',
      delete: '删除',
      confidence: '置信度',
      recorded: '已录制',
      conversations: '对话',
      totalDuration: '总时长',
      noConversationsFound: '未找到对话',
      noConversationsYet: '还没有对话',
      tryAdjustingSearch: '尝试调整您的搜索词',
      startConversationToSee: '开始对话以在此查看',
      youWrote: '您写了',
      youSpoke: '您说了',
      partnerWrote: '对方写了',
      partnerHears: '对方听到',
      noOriginalText: '没有原文',
      noTranslation: '没有翻译',
      download: '下载',
      generateAudioFor: '生成音频 ',
      generating: '生成中...',

      // Word Usage Modal
      wordUsageAndQuotas: '单词使用量和配额',
      trackTranslationUsage: '跟踪您的翻译使用情况并管理配额',
      currentUsage: '当前使用量',
      wordsUsage: '单词使用量',
      switchToTotalView: '切换到总计视图',
      switchToRemainingView: '切换到剩余视图',
      of: '/',
      lastReset: '上次重置：',
      quotaSettings: '配额设置',
      enableWordQuotas: '启用单词配额',
      setLimitsOnWordUsage: '设置单词使用限制',
      wordLimit: '单词限制',
      maximumWordsPerResetPeriod: '每个重置期的最大单词数',
      resetPeriod: '重置期',
      daily: '每日',
      weekly: '每周',
      monthly: '每月',
      neverManualResetOnly: '从不（仅手动重置）',
      howOftenUsageStatsReset: '使用统计重置频率',

      // Image Usage Modal
      trackImageProcessingUsage: '跟踪您的图像处理使用情况',
      imagesUsage: '图像使用量',
      nearLimit: '接近限制',
      warning: '警告',
      good: '良好',
      imagesUsed: '已使用图像',
      remaining: '剩余',
      imagesRemaining: '个图像剩余',
      imagesUsedText: '个图像已使用',
      imagesAvailable: '个图像可用',
      statistics: '统计',
      totalImagesProcessed: '处理的总图像数：',
      lastActive: '最后活动：',
      never: '从未',
      accountCreated: '账户创建日期：',
      imageLimitReached: '图像限制已达到',
      imageLimitReachedMessage: '您已达到图像处理限制。请购买更多图像以继续。',
      loadingImageUsageData: '正在加载图像使用数据...',

      // Status Messages
      hello: '你好',
      processing: '处理中...',
      recording: '录音中...',
      translating: '翻译中...',
      error: '错误',
      success: '成功',

      // Success Messages
      wordsAddedSuccessfully: '已成功将 {0} 个单词添加到您的账户！',
      imagesAddedSuccessfully: '已成功将 {0} 张图片添加到您的账户！',
      textExtractedSuccessfully: '成功从图片中提取文本！',
      audioGeneratedSuccessfully: '音频生成成功！',
      originalAudioGeneratedSuccessfully: '原始音频生成成功！',
      usageStatsResetSuccessfully: '使用统计已成功重置。',
      purchaseSuccessful: '购买成功！',
      paymentSuccessful: '付款成功！',

      // Error Messages
      wordLimitExceeded: '单词限制已超出',
      wordLimitExceededTitle: '单词限制已超出',
      wordLimitExceededMessage: '单词限制已超出。请购买更多单词以继续。',
      wordLimitExceededDetail: "You've used {0} out of {1} words. Please buy more words to continue.",
      textExceedsWordLimit: '文本超过200个单词限制。请缩短您的消息。',
      pleaseBuyMoreWords: '请购买更多单词',
      pleaseBuyMoreWordsToContinue: '请购买更多单词以继续。',
      purchaseFailed: '购买失败',
      purchaseFailedTryAgain: '购买失败。请重试。',
      readImageFailed: '无法从图片中读取文本',
      noTextFoundInImage: '图片中未找到文本',
      translationFailed: '翻译失败',
      translationFailedTryAgain: '翻译失败。请重试。',
      wordsRemainingWarning: '警告：您只剩下 {0} 个单词（已使用 {1}%）。请考虑尽快购买更多单词。',
      approachingWordLimit: '接近单词限制',

      // Continuous Conversation Modal
      continuousConversation: '连续对话',
      bidirectionalConversation: '双向对话',
      readyToTranscribe: '准备转录',
      transcribing: '转录中...',
      startTranscribing: '开始转录',
      stopTranscribing: '停止转录',
      transcripts: '转录',
      conversation: '对话',
      noTranscriptsYet: '还没有转录。开始转录以开始！',
      buyMoreWords: '购买更多单词',
      dismiss: '关闭',
      lectureMode: '讲座模式',
      lectureModeDescription: '每20秒录制一次以获得更好的翻译上下文',
      advancedModel: '高级翻译模型',
      advancedModelDescription: '使用 gpt-5.4-mini。可能会较慢。',
      liveTranslation: "实时传译",
      liveTranslationDescription: "说话的同时进行翻译（gpt-realtime-translate），用于连续对话和广播。可识别：英语、韩语、普通话、俄语、哈萨克语、乌兹别克语、波斯语、西班牙语。语音输出：英语、韩语、普通话、俄语、西班牙语。",
      liveTranslationFallback: "实时传译不支持 {0} → {1}，将改用标准翻译。",
      liveTranslationNotSupported: "实时传译不支持",

      // Broadcast Translation Modal
      broadcastTranslation: '广播翻译',
      speaker: '发言者',
      listener: '听众',
      sourceLanguage: '源语言',
      sourceLanguageSet: '源语言从您的部分设置',
      targetLanguage: '目标语言',
      addLanguage: '添加语言',
      broadcastCode: '广播代码',
      shareCode: '与听众分享此代码',
      startBroadcast: '开始广播',
      stopBroadcast: '停止广播',
      enterCode: '输入广播代码',
      selectLanguage: '选择语言',
      joinBroadcast: '加入广播',
      listeningTo: '正在收听',
      leaveBroadcast: '离开广播',
      messages: '消息',
      waitingForMessages: '等待消息...',
      availableLanguages: '可用语言',
      conversationTopic: '对话主题（可选）',
      topicPlaceholder: '例如：医疗咨询、商务会议、旅行计划...',
      topicHint: '添加主题有助于提高翻译准确性',
      audioOn: '音频：开启',
      audioOff: '音频：关闭',
      speaking: '正在说话...',
      wordCountWarning: '注意：每增加一种语言，单词计数将成倍增加（例如：2种语言 = 2倍，3种语言 = 3倍）',

      // Auth0 Error Messages
      auth0LoginFailed: 'Auth0 登录失败',
      auth0LoginTimedOut: 'Auth0 登录超时',
      auth0RegistrationFailed: 'Auth0 注册失败',
      auth0RegistrationTimedOut: 'Auth0 注册超时',
      invalidAuthResponse: '无效的认证响应',
      facebookLoginDenied: 'Facebook 登录被拒绝',
      facebookPermissionsRequired: '需要 Facebook 权限',
      loginRequired: '需要登录',
      notMobilePlatform: '此功能仅在移动平台上可用',
      transcriptionTimeout: '转录超时',
      transcriptionTimeoutTitle: '转录超时',
      transcriptionTimeoutMessage: '转录请求耗时过长。正在重试...',
      transcriptionFailed: '转录失败。请重试。',
      retryingTranscription: '正在重试转录...',

      // Support Page
      supportAndFeedback: '支持与反馈',
      supportHeaderSubtitle: '我们在这里为您提供帮助，并希望听到您的声音',
      supportThankYou: '感谢您使用',
      supportFeedbackInvaluable: '您的反馈对我们非常宝贵',
      supportImproveServices: '我们将继续改进翻译服务',
      supportMultilingualCommunication: '并使多语言交流',
      supportQuestionsSuggestions: '无论您有疑问、建议、',
      supportTechnicalSupport: '需要技术支持、',
      supportShareExperience: '或想分享您使用TilHub的体验，',
      supportLoveToHear: '我们都希望听到您的声音。',
      supportGetInTouch: '联系我们',
      supportContactUs: '如需反馈、问题或支持，请通过以下方式联系我们：',
      supportSendEmail: '发送邮件',
      supportCopyEmailAddress: '复制邮箱地址',
      supportEmailCopied: '邮箱地址已复制到剪贴板！',
      supportBackToApp: '返回应用',
      supportDeleteAccount: '删除您的账户',
      supportDeleteAccountDescription: '您可以随时请求删除您的账户和所有相关数据。',
      supportRequestAccountDeletion: '请求删除账户',
      supportDeleteAccountConfirmation: 'Are you sure you want to delete your account? This action cannot be undone. All your data will be permanently deleted.',
      supportDeleteAccountFinalConfirm: 'This is your final warning. Your account and all data will be permanently deleted. Are you absolutely sure?',
      supportAccountDeleted: 'Your account has been deleted successfully. You will be logged out now.',
      supportDeleteAccountError: 'Failed to delete account. Please try again or contact support.',
      supportDeletingAccount: 'Deleting...',
      support: '支持',
    },

    'zh-CN': {
      // Navigation
      languages: '语言',
      languageSettings: '语言设置',
      voiceType: '语音类型',
      account: '账户',
      profile: '个人资料',
      settings: '设置',
      history: '历史记录',
      wordUsage: '单词使用量',
      imageUsage: '图片使用量',
      analytics: '分析',
      buyWords: '购买翻译数据',
      buyImages: '购买图像数据',
      buyDataFromWebapp: '从网页应用购买数据',
      login: '登录',
      register: '注册',
      logout: '退出',

      // Buy Modals
      buyWordsTitle: '购买单词',
      buyWordsSubtitle: '购买额外的翻译数据',
      buyImagesTitle: '购买图像',
      buyImagesSubtitle: '购买额外的图像处理积分',
      conversationTopicPlaceholder: '对话主题（例如：牙科讲座、医学会议）',
      conversationTopicHint: '可选：有助于提高特定主题的翻译准确性',
      selectWordPackage: '选择单词包',
      selectImagePackage: '选择图像包',
      oneTimePurchase: '一次性购买',
      buyMoreSaveMore: '买得越多，省得越多！',
      popular: '热门',
      data: '数据',
      wordCredits: '单词积分',
      imageProcessingCredits: '图像处理积分',
      perfectForTesting: '测试完美',
      save: '节省',
      selectAnOption: '选择选项',
      howItWorks: '工作原理：',
      oneTimePurchaseAddsWords: '一次性购买将单词永久添加到您的账户',
      wordsConsumedAsYouUse: '使用翻译功能时会消耗单词',
      noExpirationUseWords: '无过期 - 随时使用单词',
      oneTimePurchaseAddsImages: '一次性购买将图像永久添加到您的账户',
      imagesConsumedWhenYouUseOCR: '使用文本提取时会消耗图像',
      noExpirationUseImages: '无过期 - 随时使用图像',

      // Language Selection
      yourLanguage: '您的语言:',
      partnerLanguage: '对方的语言:',
      speakingSpeed: '语速:',
      slow: '慢',
      fast: '快',

      // Main App
      translation: '翻译',
      start: '开始',
      end: '结束',
      clear: '清除',
      clearHistory: '清除历史',
      showAll: '显示全部',
      readImage: '读取图片',
      translate: '翻译',
      typeMessage: '在此输入您的消息...',
      typePartnerMessage: '在此输入对方的消息...',
      maxWords: '最多200字',
      words: '字',
      characters: '字符',

      // Conversation
      youSpeakLabel: '您说',
      partnerSpeaksLabel: '对方说',

      // History Modal
      conversationHistory: '对话历史',
      searchConversations: '搜索对话...',
      allAudioFiles: '所有音频文件',
      clearAll: '全部清除',
      downloadAll: '全部下载',
      play: '播放',
      delete: '删除',
      confidence: '置信度',
      recorded: '已录制',
      conversations: '对话',
      totalDuration: '总时长',
      noConversationsFound: '未找到对话',
      noConversationsYet: '还没有对话',
      tryAdjustingSearch: '尝试调整您的搜索词',
      startConversationToSee: '开始对话以在此查看',
      youWrote: '您写了',
      youSpoke: '您说了',
      partnerWrote: '对方写了',
      partnerHears: '对方听到',
      noOriginalText: '没有原文',
      noTranslation: '没有翻译',
      download: '下载',
      generateAudioFor: '生成音频 ',
      generating: '生成中...',

      // Word Usage Modal
      wordUsageAndQuotas: '单词使用量和配额',
      trackTranslationUsage: '跟踪您的翻译使用情况并管理配额',
      currentUsage: '当前使用量',
      wordsUsage: '单词使用量',
      switchToTotalView: '切换到总计视图',
      switchToRemainingView: '切换到剩余视图',
      of: '/',
      lastReset: '上次重置：',
      quotaSettings: '配额设置',
      enableWordQuotas: '启用单词配额',
      setLimitsOnWordUsage: '设置单词使用限制',
      wordLimit: '单词限制',
      maximumWordsPerResetPeriod: '每个重置期的最大单词数',
      resetPeriod: '重置期',
      daily: '每日',
      weekly: '每周',
      monthly: '每月',
      neverManualResetOnly: '从不（仅手动重置）',
      howOftenUsageStatsReset: '使用统计重置频率',

      // Image Usage Modal
      trackImageProcessingUsage: '跟踪您的图像处理使用情况',
      imagesUsage: '图像使用量',
      nearLimit: '接近限制',
      warning: '警告',
      good: '良好',
      imagesUsed: '已使用图像',
      remaining: '剩余',
      imagesRemaining: '个图像剩余',
      imagesUsedText: '个图像已使用',
      imagesAvailable: '个图像可用',
      statistics: '统计',
      totalImagesProcessed: '处理的总图像数：',
      lastActive: '最后活动：',
      never: '从未',
      accountCreated: '账户创建日期：',
      imageLimitReached: '图像限制已达到',
      imageLimitReachedMessage: '您已达到图像处理限制。请购买更多图像以继续。',
      loadingImageUsageData: '正在加载图像使用数据...',

      // Status Messages
      hello: '你好',
      processing: '处理中...',
      recording: '录音中...',
      translating: '翻译中...',
      error: '错误',
      success: '成功',

      // Success Messages (using English as placeholder - add translations later)
      wordsAddedSuccessfully: 'Successfully added {0} words to your account!',
      imagesAddedSuccessfully: 'Successfully added {0} images to your account!',
      textExtractedSuccessfully: 'Text extracted from image successfully!',
      audioGeneratedSuccessfully: 'Audio generated successfully!',
      originalAudioGeneratedSuccessfully: 'Original audio generated successfully!',
      usageStatsResetSuccessfully: 'Usage statistics have been reset successfully.',
      purchaseSuccessful: 'Purchase successful!',
      paymentSuccessful: 'Payment successful!',

      // Error Messages (using English as placeholder - add translations later)
      wordLimitExceeded: 'Word limit exceeded',
      wordLimitExceededTitle: 'Word Limit Exceeded',
      wordLimitExceededMessage: '单词限制已超出。请购买更多单词以继续。',
      wordLimitExceededDetail: '您已使用 {0} 个单词，共 {1} 个单词。请购买更多单词以继续。',
      textExceedsWordLimit: '文本超过200个单词限制。请缩短您的消息。',
      pleaseBuyMoreWords: '请购买更多单词',
      pleaseBuyMoreWordsToContinue: '请购买更多单词以继续。',
      purchaseFailed: '购买失败',
      purchaseFailedTryAgain: '购买失败。请重试。',
      readImageFailed: '无法从图片中读取文本',
      noTextFoundInImage: '图片中未找到文本',
      translationFailed: '翻译失败',
      translationFailedTryAgain: '翻译失败。请重试。',
      wordsRemainingWarning: '警告：您只剩下 {0} 个单词（已使用 {1}%）。请考虑尽快购买更多单词。',
      approachingWordLimit: '接近单词限制',

      // Continuous Conversation Modal
      continuousConversation: '连续对话',
      bidirectionalConversation: '双向对话',
      readyToTranscribe: '准备转录',
      transcribing: '转录中...',
      startTranscribing: '开始转录',
      stopTranscribing: '停止转录',
      transcripts: '转录',
      conversation: '对话',
      noTranscriptsYet: '还没有转录。开始转录以开始！',
      buyMoreWords: '购买更多单词',
      dismiss: '关闭',
      lectureMode: '讲座模式',
      lectureModeDescription: '每20秒录制一次以获得更好的翻译上下文',
      advancedModel: '高级翻译模型',
      advancedModelDescription: '使用 gpt-5.4-mini。可能会较慢。',
      liveTranslation: "实时传译",
      liveTranslationDescription: "说话的同时进行翻译（gpt-realtime-translate），用于连续对话和广播。可识别：英语、韩语、普通话、俄语、哈萨克语、乌兹别克语、波斯语、西班牙语。语音输出：英语、韩语、普通话、俄语、西班牙语。",
      liveTranslationFallback: "实时传译不支持 {0} → {1}，将改用标准翻译。",
      liveTranslationNotSupported: "实时传译不支持",

      // Broadcast Translation Modal
      broadcastTranslation: '广播翻译',
      speaker: '发言者',
      listener: '听众',
      sourceLanguage: '源语言',
      sourceLanguageSet: '源语言从您的部分设置',
      targetLanguage: '目标语言',
      addLanguage: '添加语言',
      broadcastCode: '广播代码',
      shareCode: '与听众分享此代码',
      startBroadcast: '开始广播',
      stopBroadcast: '停止广播',
      enterCode: '输入广播代码',
      selectLanguage: '选择语言',
      joinBroadcast: '加入广播',
      listeningTo: '正在收听',
      leaveBroadcast: '离开广播',
      messages: '消息',
      waitingForMessages: '等待消息...',
      availableLanguages: '可用语言',
      conversationTopic: '对话主题（可选）',
      topicPlaceholder: '例如：医疗咨询、商务会议、旅行计划...',
      topicHint: '添加主题有助于提高翻译准确性',
      audioOn: '音频：开启',
      audioOff: '音频：关闭',
      speaking: '正在说话...',
      wordCountWarning: '注意：每增加一种语言，单词计数将成倍增加（例如：2种语言 = 2倍，3种语言 = 3倍）',

      // Auth0 Error Messages
      auth0LoginFailed: 'Auth0 登录失败',
      auth0LoginTimedOut: 'Auth0 登录超时',
      auth0RegistrationFailed: 'Auth0 注册失败',
      auth0RegistrationTimedOut: 'Auth0 注册超时',
      invalidAuthResponse: '无效的认证响应',
      facebookLoginDenied: 'Facebook 登录被拒绝',
      facebookPermissionsRequired: '需要 Facebook 权限',
      loginRequired: '需要登录',
      notMobilePlatform: '此功能仅在移动平台上可用',
      transcriptionTimeout: '转录超时',
      transcriptionTimeoutTitle: '转录超时',
      transcriptionTimeoutMessage: '转录请求耗时过长。正在重试...',
      transcriptionFailed: '转录失败。请重试。',
      retryingTranscription: '正在重试转录...',

      // Support Page
      supportAndFeedback: '支持与反馈',
      supportHeaderSubtitle: '我们在这里为您提供帮助，并希望听到您的声音',
      supportThankYou: '感谢您使用',
      supportFeedbackInvaluable: '您的反馈对我们非常宝贵',
      supportImproveServices: '我们将继续改进翻译服务',
      supportMultilingualCommunication: '并使多语言交流',
      supportQuestionsSuggestions: '无论您有疑问、建议、',
      supportTechnicalSupport: '需要技术支持、',
      supportShareExperience: '或想分享您使用TilHub的体验，',
      supportLoveToHear: '我们都希望听到您的声音。',
      supportGetInTouch: '联系我们',
      supportContactUs: '如需反馈、问题或支持，请通过以下方式联系我们：',
      supportSendEmail: '发送邮件',
      supportCopyEmailAddress: '复制邮箱地址',
      supportEmailCopied: '邮箱地址已复制到剪贴板！',
      supportBackToApp: '返回应用',
      supportDeleteAccount: '删除您的账户',
      supportDeleteAccountDescription: '您可以随时请求删除您的账户和所有相关数据。',
      supportRequestAccountDeletion: '请求删除账户',
      supportDeleteAccountConfirmation: 'Are you sure you want to delete your account? This action cannot be undone. All your data will be permanently deleted.',
      supportDeleteAccountFinalConfirm: 'This is your final warning. Your account and all data will be permanently deleted. Are you absolutely sure?',
      supportAccountDeleted: 'Your account has been deleted successfully. You will be logged out now.',
      supportDeleteAccountError: 'Failed to delete account. Please try again or contact support.',
      supportDeletingAccount: 'Deleting...',
      support: '支持',
    },

    'zh-HK': {
      // Navigation
      languages: '語言',
      languageSettings: '語言設定',
      voiceType: '語音類型',
      account: '賬戶',
      profile: '個人資料',
      settings: '設定',
      history: '歷史記錄',
      wordUsage: '單詞使用量',
      imageUsage: '圖片使用量',
      analytics: '分析',
      buyWords: '購買翻譯數據',
      buyImages: '購買圖像數據',
      buyDataFromWebapp: '從網頁應用購買數據',
      login: '登錄',
      register: '註冊',
      logout: '退出',

      // Buy Modals
      buyWordsTitle: '購買單詞',
      buyWordsSubtitle: '購買額外的翻譯數據',
      buyImagesTitle: '購買圖像',
      buyImagesSubtitle: '購買額外的圖像處理積分',
      conversationTopicPlaceholder: '對話主題（例如：牙科講座、醫學會議）',
      conversationTopicHint: '可選：有助於提高特定主題的翻譯準確性',
      selectWordPackage: '選擇單詞包',
      selectImagePackage: '選擇圖像包',
      oneTimePurchase: '一次性購買',
      buyMoreSaveMore: '買得越多，省得越多！',
      popular: '熱門',
      data: '數據',
      wordCredits: '單詞積分',
      imageProcessingCredits: '圖像處理積分',
      perfectForTesting: '測試完美',
      save: '節省',
      selectAnOption: '選擇選項',
      howItWorks: '工作原理：',
      oneTimePurchaseAddsWords: '一次性購買將單詞永久添加到您的賬戶',
      wordsConsumedAsYouUse: '使用翻譯功能時會消耗單詞',
      noExpirationUseWords: '無過期 - 隨時使用單詞',
      oneTimePurchaseAddsImages: '一次性購買將圖像永久添加到您的賬戶',
      imagesConsumedWhenYouUseOCR: '使用文本提取時會消耗圖像',
      noExpirationUseImages: '無過期 - 隨時使用圖像',

      // Language Selection
      yourLanguage: '您的語言:',
      partnerLanguage: '對方的語言:',
      speakingSpeed: '語速:',
      slow: '慢',
      fast: '快',

      // Main App
      translation: '翻譯',
      start: '開始',
      end: '結束',
      clear: '清除',
      clearHistory: '清除歷史',
      showAll: '顯示全部',
      readImage: '讀取圖片',
      translate: '翻譯',
      typeMessage: '在此輸入您的消息...',
      typePartnerMessage: '在此輸入對方的消息...',
      maxWords: '最多200字',
      words: '字',
      characters: '字符',

      // Conversation
      youSpeakLabel: '您說',
      partnerSpeaksLabel: '對方說',

      // History Modal
      conversationHistory: '對話歷史',
      searchConversations: '搜索對話...',
      allAudioFiles: '所有音頻文件',
      clearAll: '全部清除',
      downloadAll: '全部下載',
      play: '播放',
      delete: '刪除',
      confidence: '置信度',
      recorded: '已錄製',
      conversations: '對話',
      totalDuration: '總時長',
      noConversationsFound: '未找到對話',
      noConversationsYet: '還沒有對話',
      tryAdjustingSearch: '嘗試調整您的搜索詞',
      startConversationToSee: '開始對話以在此查看',
      youWrote: '您寫了',
      youSpoke: '您說了',
      partnerWrote: '對方寫了',
      partnerHears: '對方聽到',
      noOriginalText: '沒有原文',
      noTranslation: '沒有翻譯',
      download: '下載',
      generateAudioFor: '生成音頻 ',
      generating: '生成中...',

      // Word Usage Modal
      wordUsageAndQuotas: '單詞使用量和配額',
      trackTranslationUsage: '跟蹤您的翻譯使用情況並管理配額',
      currentUsage: '當前使用量',
      wordsUsage: '單詞使用量',
      switchToTotalView: '切換到總計視圖',
      switchToRemainingView: '切換到剩餘視圖',
      of: '/',
      lastReset: '上次重置：',
      quotaSettings: '配額設定',
      enableWordQuotas: '啟用單詞配額',
      setLimitsOnWordUsage: '設定單詞使用限制',
      wordLimit: '單詞限制',
      maximumWordsPerResetPeriod: '每個重置期的最大單詞數',
      resetPeriod: '重置期',
      daily: '每日',
      weekly: '每週',
      monthly: '每月',
      neverManualResetOnly: '從不（僅手動重置）',
      howOftenUsageStatsReset: '使用統計重置頻率',

      // Image Usage Modal
      trackImageProcessingUsage: '追蹤您的圖像處理使用情況',
      imagesUsage: '圖像使用量',
      nearLimit: '接近限制',
      warning: '警告',
      good: '良好',
      imagesUsed: '已使用圖像',
      remaining: '剩餘',
      imagesRemaining: '個圖像剩餘',
      imagesUsedText: '個圖像已使用',
      imagesAvailable: '個圖像可用',
      statistics: '統計',
      totalImagesProcessed: '處理的總圖像數：',
      lastActive: '最後活動：',
      never: '從未',
      accountCreated: '賬戶創建日期：',
      imageLimitReached: '圖像限制已達到',
      imageLimitReachedMessage: '您已達到圖像處理限制。請購買更多圖像以繼續。',
      loadingImageUsageData: '正在加載圖像使用數據...',

      // Status Messages
      hello: '你好',
      processing: '處理中...',
      recording: '錄音中...',
      translating: '翻譯中...',
      error: '錯誤',
      success: '成功',

      // Success Messages (using English as placeholder - add translations later)
      wordsAddedSuccessfully: 'Successfully added {0} words to your account!',
      imagesAddedSuccessfully: 'Successfully added {0} images to your account!',
      textExtractedSuccessfully: 'Text extracted from image successfully!',
      audioGeneratedSuccessfully: 'Audio generated successfully!',
      originalAudioGeneratedSuccessfully: 'Original audio generated successfully!',
      usageStatsResetSuccessfully: 'Usage statistics have been reset successfully.',
      purchaseSuccessful: 'Purchase successful!',
      paymentSuccessful: 'Payment successful!',

      // Error Messages (using English as placeholder - add translations later)
      wordLimitExceeded: 'Word limit exceeded',
      wordLimitExceededTitle: 'Word Limit Exceeded',
      wordLimitExceededMessage: '單詞限制已超出。請購買更多單詞以繼續。',
      wordLimitExceededDetail: '您已使用 {0} 個單詞，共 {1} 個單詞。請購買更多單詞以繼續。',
      textExceedsWordLimit: '文本超過200個單詞限制。請縮短您的消息。',
      pleaseBuyMoreWords: '請購買更多單詞',
      pleaseBuyMoreWordsToContinue: '請購買更多單詞以繼續。',
      purchaseFailed: '購買失敗',
      purchaseFailedTryAgain: '購買失敗。請重試。',
      readImageFailed: '無法從圖片中讀取文本',
      noTextFoundInImage: '圖片中未找到文本',
      translationFailed: '翻譯失敗',
      translationFailedTryAgain: '翻譯失敗。請重試。',
      wordsRemainingWarning: '警告：您只剩下 {0} 個單詞（已使用 {1}%）。請考慮盡快購買更多單詞。',
      approachingWordLimit: '接近單詞限制',

      // Continuous Conversation Modal
      continuousConversation: '連續對話',
      bidirectionalConversation: '雙向對話',
      readyToTranscribe: '準備轉錄',
      transcribing: '轉錄中...',
      startTranscribing: '開始轉錄',
      stopTranscribing: '停止轉錄',
      transcripts: '轉錄',
      conversation: '對話',
      noTranscriptsYet: '還沒有轉錄。開始轉錄以開始！',
      buyMoreWords: '購買更多單詞',
      dismiss: '關閉',
      lectureMode: '講座模式',
      lectureModeDescription: '每20秒錄製一次以獲得更好的翻譯上下文',
      advancedModel: '進階翻譯模型',
      advancedModelDescription: '使用 gpt-5.4-mini。可能會較慢。',
      liveTranslation: "即時傳譯",
      liveTranslationDescription: "說話的同時進行翻譯（gpt-realtime-translate），用於連續對話和廣播。可識別：英語、韓語、普通話、俄語、哈薩克語、烏茲別克語、波斯語、西班牙語。語音輸出：英語、韓語、普通話、俄語、西班牙語。",
      liveTranslationFallback: "即時傳譯不支援 {0} → {1}，將改用標準翻譯。",
      liveTranslationNotSupported: "即時傳譯不支援",

      // Broadcast Translation Modal
      broadcastTranslation: '廣播翻譯',
      speaker: '發言者',
      listener: '聽眾',
      sourceLanguage: '源語言',
      sourceLanguageSet: '源語言從您的部分設置',
      targetLanguage: '目標語言',
      addLanguage: '添加語言',
      broadcastCode: '廣播代碼',
      shareCode: '與聽眾分享此代碼',
      startBroadcast: '開始廣播',
      stopBroadcast: '停止廣播',
      enterCode: '輸入廣播代碼',
      selectLanguage: '選擇語言',
      joinBroadcast: '加入廣播',
      listeningTo: '正在收聽',
      leaveBroadcast: '離開廣播',
      messages: '消息',
      waitingForMessages: '等待消息...',
      availableLanguages: '可用語言',
      conversationTopic: '對話主題（可選）',
      topicPlaceholder: '例如：醫療諮詢、商務會議、旅行計劃...',
      topicHint: '添加主題有助於提高翻譯準確性',
      audioOn: '音頻：開啟',
      audioOff: '音頻：關閉',
      speaking: '正在說話...',
      wordCountWarning: '注意：每增加一種語言，單詞計數將成倍增加（例如：2種語言 = 2倍，3種語言 = 3倍）',

      // Auth0 Error Messages
      auth0LoginFailed: 'Auth0 登錄失敗',
      auth0LoginTimedOut: 'Auth0 登錄超時',
      auth0RegistrationFailed: 'Auth0 註冊失敗',
      auth0RegistrationTimedOut: 'Auth0 註冊超時',
      invalidAuthResponse: '無效的認證響應',
      facebookLoginDenied: 'Facebook 登錄被拒絕',
      facebookPermissionsRequired: '需要 Facebook 權限',
      loginRequired: '需要登錄',
      notMobilePlatform: '此功能僅在移動平台上可用',
      transcriptionTimeout: '轉錄超時',
      transcriptionTimeoutTitle: '轉錄超時',
      transcriptionTimeoutMessage: '轉錄請求耗時過長。正在重試...',
      transcriptionFailed: '轉錄失敗。請重試。',
      retryingTranscription: '正在重試轉錄...',

      // Support Page
      supportAndFeedback: '支援與反饋',
      supportHeaderSubtitle: '我們在這裡為您提供幫助，並希望聽到您的聲音',
      supportThankYou: '感謝您使用',
      supportFeedbackInvaluable: '您的反饋對我們非常寶貴',
      supportImproveServices: '我們將繼續改進翻譯服務',
      supportMultilingualCommunication: '並使多語言交流',
      supportQuestionsSuggestions: '無論您有疑問、建議、',
      supportTechnicalSupport: '需要技術支援、',
      supportShareExperience: '或想分享您使用TilHub的體驗，',
      supportLoveToHear: '我們都希望聽到您的聲音。',
      supportGetInTouch: '聯絡我們',
      supportContactUs: '如需反饋、問題或支援，請通過以下方式聯絡我們：',
      supportSendEmail: '發送郵件',
      supportCopyEmailAddress: '複製郵箱地址',
      supportEmailCopied: '郵箱地址已複製到剪貼板！',
      supportBackToApp: '返回應用',
      supportDeleteAccount: '刪除您的帳戶',
      supportDeleteAccountDescription: '您可以隨時請求刪除您的帳戶和所有相關數據。',
      supportRequestAccountDeletion: '請求刪除帳戶',
      supportDeleteAccountConfirmation: 'Are you sure you want to delete your account? This action cannot be undone. All your data will be permanently deleted.',
      supportDeleteAccountFinalConfirm: 'This is your final warning. Your account and all data will be permanently deleted. Are you absolutely sure?',
      supportAccountDeleted: 'Your account has been deleted successfully. You will be logged out now.',
      supportDeleteAccountError: 'Failed to delete account. Please try again or contact support.',
      supportDeletingAccount: 'Deleting...',
      support: '支援',
    },

    ru: {
      // Navigation
      languages: 'Языки',
      languageSettings: 'Настройки языка',
      voiceType: 'Тип голоса',
      account: 'Аккаунт',
      profile: 'Профиль',
      settings: 'Настройки',
      history: 'История',
      wordUsage: 'Использование слов',
      imageUsage: 'Использование изображений',
      analytics: 'Аналитика',
      buyWords: 'Купить данные перевода',
      buyImages: 'Купить данные изображения',
      buyDataFromWebapp: 'Купить данные через веб-приложение',
      login: 'Войти',
      register: 'Регистрация',
      logout: 'Выйти',

      // Buy Modals
      buyWordsTitle: 'Купить слова',
      buyWordsSubtitle: 'Приобрести дополнительные данные для перевода',
      buyImagesTitle: 'Купить изображения',
      buyImagesSubtitle:
        'Приобрести дополнительные кредиты для обработки изображений',
      conversationTopicPlaceholder: 'Тема разговора (например, лекция по стоматологии, медицинская конференция)',
      conversationTopicHint: 'Необязательно: помогает улучшить точность перевода для конкретных тем',
      selectWordPackage: 'Выберите пакет слов',
      selectImagePackage: 'Выберите пакет изображений',
      oneTimePurchase: 'Разовая покупка',
      buyMoreSaveMore: 'Покупайте больше, экономьте больше!',
      popular: 'Популярный',
      data: 'данные',
      wordCredits: 'кредиты слов',
      imageProcessingCredits: 'кредиты обработки изображений',
      perfectForTesting: 'Идеально для тестирования',
      save: 'Экономия',
      selectAnOption: 'Выберите опцию',
      howItWorks: 'Как это работает:',
      oneTimePurchaseAddsWords:
        'Разовая покупка навсегда добавляет слова в ваш аккаунт',
      wordsConsumedAsYouUse:
        'Слова расходуются при использовании функций перевода',
      noExpirationUseWords:
        'Без срока действия - используйте слова когда нужно',
      oneTimePurchaseAddsImages:
        'Разовая покупка навсегда добавляет изображения в ваш аккаунт',
      imagesConsumedWhenYouUseOCR:
        'Изображения расходуются при использовании извлечения текста',
      noExpirationUseImages:
        'Без срока действия - используйте изображения когда нужно',

      // Language Selection
      yourLanguage: 'Ваш язык:',
      partnerLanguage: 'Язык партнера:',
      speakingSpeed: 'Скорость речи:',
      slow: 'Медленно',
      fast: 'Быстро',

      // Main App
      translation: 'Переводчик', // More descriptive than "Перевод" - means "Translator"
      start: 'Начать',
      end: 'Завершить',
      clear: 'Очистить',
      clearHistory: 'Очистить историю',
      showAll: 'Показать все',
      readImage: 'Читать изображение',
      translate: 'Перевести',
      typeMessage: 'Введите ваше сообщение здесь...',
      typePartnerMessage: 'Введите сообщение партнера здесь...',
      maxWords: 'макс. 200 слов',
      words: 'слов',
      characters: 'символов',

      // Conversation
      youSpeakLabel: 'Вы говорите',
      partnerSpeaksLabel: 'Партнер говорит',

      // History Modal
      conversationHistory: 'История разговоров',
      searchConversations: 'Поиск разговоров...',
      allAudioFiles: 'Все аудиофайлы',
      clearAll: 'Очистить все',
      downloadAll: 'Загрузить все',
      play: 'Воспроизвести',
      delete: 'Удалить',
      confidence: 'Уверенность',
      recorded: 'Записано',
      conversations: 'разговоры',
      totalDuration: 'общая продолжительность',
      noConversationsFound: 'Разговоры не найдены',
      noConversationsYet: 'Пока нет разговоров',
      tryAdjustingSearch: 'Попробуйте изменить условия поиска',
      startConversationToSee: 'Начните разговор, чтобы увидеть его здесь',
      youWrote: 'Вы написали',
      youSpoke: 'Вы сказали',
      partnerWrote: 'Партнер написал',
      partnerHears: 'Партнер слышит',
      noOriginalText: 'Нет исходного текста',
      noTranslation: 'Нет перевода',
      download: 'Загрузить',
      generateAudioFor: 'Создать аудио для ',
      generating: 'Генерация...',

      // Word Usage Modal
      wordUsageAndQuotas: 'Использование слов и квоты',
      trackTranslationUsage: 'Отслеживайте использование переводов и управляйте квотами',
      currentUsage: 'Текущее использование',
      wordsUsage: 'Использование слов',
      switchToTotalView: 'Переключить на общий вид',
      switchToRemainingView: 'Переключить на оставшийся вид',
      of: 'из',
      lastReset: 'Последний сброс:',
      quotaSettings: 'Настройки квоты',
      enableWordQuotas: 'Включить квоты слов',
      setLimitsOnWordUsage: 'Установить ограничения на использование слов',
      wordLimit: 'Лимит слов',
      maximumWordsPerResetPeriod: 'Максимум слов за период сброса',
      resetPeriod: 'Период сброса',
      daily: 'Ежедневно',
      weekly: 'Еженедельно',
      monthly: 'Ежемесячно',
      neverManualResetOnly: 'Никогда (только вручную)',
      howOftenUsageStatsReset: 'Как часто сбрасывается статистика использования',

      // Image Usage Modal
      trackImageProcessingUsage: 'Отслеживайте использование обработки изображений',
      imagesUsage: 'Использование изображений',
      nearLimit: 'Близко к лимиту',
      warning: 'Предупреждение',
      good: 'Хорошо',
      imagesUsed: 'Использовано изображений',
      remaining: 'Осталось',
      imagesRemaining: 'изображений осталось',
      imagesUsedText: 'изображений использовано',
      imagesAvailable: 'изображений доступно',
      statistics: 'Статистика',
      totalImagesProcessed: 'Всего обработано изображений:',
      lastActive: 'Последняя активность:',
      never: 'Никогда',
      accountCreated: 'Аккаунт создан:',
      imageLimitReached: 'Достигнут лимит изображений',
      imageLimitReachedMessage: 'Вы достигли лимита обработки изображений. Купите больше изображений, чтобы продолжить.',
      loadingImageUsageData: 'Загрузка данных об использовании изображений...',

      // Status Messages
      hello: 'Привет',
      processing: 'Обработка...',
      recording: 'Запись...',
      translating: 'Перевод...',
      error: 'Ошибка',
      success: 'Успешно',

      // Success Messages
      wordsAddedSuccessfully: 'Успешно добавлено {0} слов в ваш аккаунт!',
      imagesAddedSuccessfully: 'Успешно добавлено {0} изображений в ваш аккаунт!',
      textExtractedSuccessfully: 'Текст успешно извлечен из изображения!',
      audioGeneratedSuccessfully: 'Аудио успешно создано!',
      originalAudioGeneratedSuccessfully: 'Оригинальное аудио успешно создано!',
      usageStatsResetSuccessfully: 'Статистика использования успешно сброшена.',
      purchaseSuccessful: 'Покупка успешна!',
      paymentSuccessful: 'Оплата успешна!',

      // Error Messages
      wordLimitExceeded: 'Лимит слов превышен',
      wordLimitExceededTitle: 'Лимит слов превышен',
      wordLimitExceededMessage: 'Лимит слов превышен. Пожалуйста, купите больше слов, чтобы продолжить.',
      wordLimitExceededDetail: 'Лимит слов превышен! Вы использовали {0} из {1} слов. Пожалуйста, купите больше слов, чтобы продолжить.',
      textExceedsWordLimit: 'Текст превышает лимит в 200 слов. Пожалуйста, сократите ваше сообщение.',
      pleaseBuyMoreWords: 'Пожалуйста, купите больше слов',
      pleaseBuyMoreWordsToContinue: 'Пожалуйста, купите больше слов, чтобы продолжить.',
      purchaseFailed: 'Покупка не удалась',
      purchaseFailedTryAgain: 'Покупка не удалась. Пожалуйста, попробуйте снова.',
      readImageFailed: 'Не удалось прочитать текст из изображения',
      noTextFoundInImage: 'Текст в изображении не найден',
      translationFailed: 'Перевод не удался',
      translationFailedTryAgain: 'Перевод не удался. Пожалуйста, попробуйте снова.',
      wordsRemainingWarning: 'Предупреждение: У вас осталось только {0} слов ({1}% использовано). Рассмотрите возможность покупки больше слов в ближайшее время.',
      approachingWordLimit: 'Приближается лимит слов',

      // Continuous Conversation Modal
      continuousConversation: 'Непрерывный разговор',
      bidirectionalConversation: 'Двусторонний разговор',
      readyToTranscribe: 'Готов к транскрипции',
      transcribing: 'Транскрипция...',
      startTranscribing: 'Начать транскрипцию',
      stopTranscribing: 'Остановить транскрипцию',
      transcripts: 'Транскрипции',
      conversation: 'Разговор',
      noTranscriptsYet: 'Транскрипций пока нет. Начните транскрипцию, чтобы начать!',
      buyMoreWords: 'Купить больше слов',
      dismiss: 'Закрыть',
      lectureMode: 'Режим лекции',
      lectureModeDescription: 'Записывать каждые 20 секунд для лучшего контекста перевода',
      advancedModel: 'Расширенная модель перевода',
      advancedModelDescription: 'Использует gpt-5.4-mini. Может быть медленнее.',
      liveTranslation: "Синхронный перевод",
      liveTranslationDescription: "Переводит во время речи (gpt-realtime-translate) в режимах «Непрерывный разговор» и «Трансляция». Распознаёт: английский, корейский, китайский (мандарин), русский, казахский, узбекский, фарси, испанский. Озвучивает: английский, корейский, китайский (мандарин), русский, испанский.",
      liveTranslationFallback: "Синхронный перевод не поддерживает {0} → {1}. Используется обычный перевод.",
      liveTranslationNotSupported: "недоступно для синхронного перевода",

      // Broadcast Translation Modal
      broadcastTranslation: 'Трансляция перевода',
      speaker: 'Докладчик',
      listener: 'Слушатель',
      sourceLanguage: 'Исходный язык',
      sourceLanguageSet: 'Исходный язык установлен из вашей секции',
      targetLanguage: 'Целевой язык',
      addLanguage: 'Добавить язык',
      broadcastCode: 'Код трансляции',
      shareCode: 'Поделитесь этим кодом со слушателями',
      startBroadcast: 'Начать трансляцию',
      stopBroadcast: 'Остановить трансляцию',
      enterCode: 'Введите код трансляции',
      selectLanguage: 'Выберите язык',
      joinBroadcast: 'Присоединиться к трансляции',
      listeningTo: 'Прослушивание',
      leaveBroadcast: 'Покинуть трансляцию',
      messages: 'Сообщения',
      waitingForMessages: 'Ожидание сообщений...',
      availableLanguages: 'Доступные языки',
      conversationTopic: 'Тема разговора (необязательно)',
      topicPlaceholder: 'Например: Медицинская консультация, Деловая встреча, Планирование поездки...',
      topicHint: 'Добавление темы помогает улучшить точность перевода',
      audioOn: 'Аудио: ВКЛ',
      audioOff: 'Аудио: ВЫКЛ',
      speaking: 'Говорит...',
      wordCountWarning: 'Примечание: Каждый дополнительный язык будет умножать количество слов (например, 2 языка = 2x слов, 3 языка = 3x слов)',

      // Auth0 Error Messages
      auth0LoginFailed: 'Ошибка входа через Auth0',
      auth0LoginTimedOut: 'Время ожидания входа через Auth0 истекло',
      auth0RegistrationFailed: 'Ошибка регистрации через Auth0',
      auth0RegistrationTimedOut: 'Время ожидания регистрации через Auth0 истекло',
      invalidAuthResponse: 'Неверный ответ аутентификации',
      facebookLoginDenied: 'Вход через Facebook отклонен',
      facebookPermissionsRequired: 'Требуются разрешения Facebook',
      loginRequired: 'Требуется вход',
      notMobilePlatform: 'Эта функция доступна только на мобильных платформах',
      transcriptionTimeout: 'Тайм-аут транскрипции',
      transcriptionTimeoutTitle: 'Тайм-аут транскрипции',
      transcriptionTimeoutMessage: 'Запрос на транскрипцию занял слишком много времени. Повторная попытка...',
      transcriptionFailed: 'Транскрипция не удалась. Пожалуйста, попробуйте снова.',
      retryingTranscription: 'Повторная попытка транскрипции...',

      // Support Page
      supportAndFeedback: 'Поддержка и отзывы',
      supportHeaderSubtitle: 'Мы здесь, чтобы помочь вам, и нам бы хотелось услышать от вас',
      supportThankYou: 'Спасибо за использование',
      supportFeedbackInvaluable: 'Ваши отзывы бесценны для нас',
      supportImproveServices: 'мы продолжаем улучшать наши услуги перевода',
      supportMultilingualCommunication: 'и облегчаем многоязычное общение',
      supportQuestionsSuggestions: 'Если у вас есть вопросы, предложения,',
      supportTechnicalSupport: 'нужна техническая поддержка,',
      supportShareExperience: 'или вы хотите поделиться своим опытом использования TilHub,',
      supportLoveToHear: 'нам бы хотелось услышать от вас.',
      supportGetInTouch: 'Связаться с нами',
      supportContactUs: 'Для отзывов, вопросов или поддержки, пожалуйста, свяжитесь с нами по адресу:',
      supportSendEmail: 'Отправить письмо',
      supportCopyEmailAddress: 'Копировать адрес электронной почты',
      supportEmailCopied: 'Адрес электронной почты скопирован в буфер обмена!',
      supportBackToApp: 'Вернуться в приложение',
      supportDeleteAccount: 'Удалить ваш аккаунт',
      supportDeleteAccountDescription: 'Вы можете запросить удаление вашего аккаунта и всех связанных данных в любое время.',
      supportRequestAccountDeletion: 'Запросить удаление аккаунта',
      supportDeleteAccountConfirmation: 'Are you sure you want to delete your account? This action cannot be undone. All your data will be permanently deleted.',
      supportDeleteAccountFinalConfirm: 'This is your final warning. Your account and all data will be permanently deleted. Are you absolutely sure?',
      supportAccountDeleted: 'Your account has been deleted successfully. You will be logged out now.',
      supportDeleteAccountError: 'Failed to delete account. Please try again or contact support.',
      supportDeletingAccount: 'Deleting...',
      support: 'Поддержка',
    },

    // Kyrgyz (Кыргызча)
    ky: {
      // Navigation
      languages: 'Тилдер',
      languageSettings: 'Тил жөндөөлөрү',
      voiceType: 'Үн түрү',
      account: 'Каттоо',
      profile: 'Профиль',
      settings: 'Жөндөөлөр',
      history: 'Тарых',
      wordUsage: 'Сөз колдонуу',
      imageUsage: 'Сүрөт колдонуу',
      analytics: 'Аналитика',
      buyWords: 'Котормо маалымат сатып алуу',
      buyImages: 'Сүрөт маалымат сатып алуу',
      buyDataFromWebapp: 'Веб-колдонмодон маалымат сатып алуу',
      login: 'Кирүү',
      register: 'Катталуу',
      logout: 'Чыгуу',

      // Buy Modals
      buyWordsTitle: 'Сөздөрдү сатып алуу',
      buyWordsSubtitle: 'Которуу үчүн кошумча маалымат сатып алуу',
      buyImagesTitle: 'Сүрөттөрдү сатып алуу',
      buyImagesSubtitle: 'Сүрөт иштетүү үчүн кошумча кредит сатып алуу',
      conversationTopicPlaceholder: 'Сүйлөшүү темасы (мисалы, тиш дарылоо лекциясы, медициналык конференция)',
      conversationTopicHint: 'Майындалбайт: белгилүү темалар үчүн которуу тактыгын жакшыртууга жардам берет',
      selectWordPackage: 'Сөз пакетин тандоо',
      selectImagePackage: 'Сүрөт пакетин тандоо',
      oneTimePurchase: 'Бир жолку сатып алуу',
      buyMoreSaveMore: 'Көбүрөөк сатып алсаңыз, көбүрөөк үнөмдөйсүз!',
      popular: 'Популярдуу',
      data: 'маалымат',
      wordCredits: 'сөз кредиттери',
      imageProcessingCredits: 'сүрөт иштетүү кредиттери',
      perfectForTesting: 'Сыноо үчүн эң жакшы',
      save: 'Үнөмдөө',
      selectAnOption: 'Опцияны тандоо',
      howItWorks: 'Кантип иштейт:',
      oneTimePurchaseAddsWords:
        'Бир жолку сатып алуу сиздин эсебиңизге сөздөрдү түбөлүккө кошот',
      wordsConsumedAsYouUse:
        'Которуу функцияларын колдонгондо сөздөр сарпталат',
      noExpirationUseWords: 'Мөөнөтү жок - керек болгондо сөздөрдү колдон',
      oneTimePurchaseAddsImages:
        'Бир жолку сатып алуу сиздин эсебиңизге сүрөттөрдү түбөлүккө кошот',
      imagesConsumedWhenYouUseOCR:
        'Текст чыгарууну колдонгондо сүрөттөр сарпталат',
      noExpirationUseImages: 'Мөөнөтү жок - керек болгондо сүрөттөрдү колдон',

      // Language Selection
      yourLanguage: 'Сиздин тилиңиз:',
      partnerLanguage: 'Жолдоштун тили:',
      speakingSpeed: 'Сүйлөө ылдамдыгы:',
      slow: 'Жай',
      fast: 'Тез',

      // Main App
      translation: 'Которуу',
      start: 'Баштоо',
      end: 'Аяктоо',
      clear: 'Тазалоо',
      clearHistory: 'Тарыхты тазалоо',
      showAll: 'Баарын көрсөтүү',
      readImage: 'Сүрөттү окуу',
      translate: 'Которуу',
      typeMessage: 'Сиздин билдирүүңүздү бул жерге териңиз...',
      typePartnerMessage: 'Жолдоштун билдирүүсүн бул жерге териңиз...',
      maxWords: 'макс 200 сөз',
      words: 'сөз',
      characters: 'символ',

      // Conversation
      youSpeakLabel: 'Сиз сүйлөйсүз',
      partnerSpeaksLabel: 'Жолдош сүйлөйт',

      // History Modal
      conversationHistory: 'Сүйлөшүү тарыхы',
      searchConversations: 'Сүйлөшүүлөрдү издөө...',
      allAudioFiles: 'Бардык аудио файлдар',
      clearAll: 'Баарын тазалоо',
      downloadAll: 'Баарын жүктөп алуу',
      play: 'Ойнотуу',
      delete: 'Жок кылуу',
      confidence: 'Ынандык',
      recorded: 'Жазылган',
      conversations: 'сүйлөшүүлөр',
      totalDuration: 'жалпы узактыгы',
      noConversationsFound: 'Сүйлөшүүлөр табылган жок',
      noConversationsYet: 'Азырынча сүйлөшүүлөр жок',
      tryAdjustingSearch: 'Издөө шарттарын тууралаңыз',
      startConversationToSee: 'Көрүү үчүн сүйлөшүү баштаңыз',
      youWrote: 'Сиз жаздыңыз',
      youSpoke: 'Сиз айттыңыз',
      partnerWrote: 'Өнөктөш жазды',
      partnerHears: 'Өнөктөш угат',
      noOriginalText: 'Түпнускасы жок',
      noTranslation: 'Котормосу жок',
      download: 'Жүктөп алуу',
      generateAudioFor: 'Аудио түзүү ',
      generating: 'Түзүлүүдө...',

      // Word Usage Modal
      wordUsageAndQuotas: 'Сөз колдонуу жана квоталар',
      trackTranslationUsage: 'Котормо колдонууңузду көзөмөлдөңүз жана квоталарды башкарыңыз',
      currentUsage: 'Учурдагы колдонуу',
      wordsUsage: 'Сөз колдонуу',
      switchToTotalView: 'Жалпы көрүнүшкө которуу',
      switchToRemainingView: 'Калган көрүнүшкө которуу',
      of: '/',
      lastReset: 'Акыркы нөлдөө:',
      quotaSettings: 'Квота жөндөөлөрү',
      enableWordQuotas: 'Сөз квоталарын иштетүү',
      setLimitsOnWordUsage: 'Сөз колдонууга чектөөлөр коюу',
      wordLimit: 'Сөз чеги',
      maximumWordsPerResetPeriod: 'Нөлдөө мезгили үчүн максималдуу сөздөр',
      resetPeriod: 'Нөлдөө мезгили',
      daily: 'Күн сайын',
      weekly: 'Жума сайын',
      monthly: 'Ай сайын',
      neverManualResetOnly: 'Эч качан (колго гана нөлдөө)',
      howOftenUsageStatsReset: 'Колдонуу статистикасы канча убакытта нөлдөнөт',

      // Image Usage Modal
      trackImageProcessingUsage: 'Сүрөт иштетүү колдонууңузду көзөмөлдөңүз',
      imagesUsage: 'Сүрөт колдонуу',
      nearLimit: 'Чекке жакын',
      warning: 'Эскертүү',
      good: 'Жакшы',
      imagesUsed: 'Колдонулган сүрөттөр',
      remaining: 'Калган',
      imagesRemaining: 'сүрөт калды',
      imagesUsedText: 'сүрөт колдонулду',
      imagesAvailable: 'сүрөт бар',
      statistics: 'Статистика',
      totalImagesProcessed: 'Жалпы иштетилген сүрөттөр:',
      lastActive: 'Акыркы активдүүлүк:',
      never: 'Эч качан',
      accountCreated: 'Аккаунт түзүлгөн:',
      imageLimitReached: 'Сүрөт чегине жетти',
      imageLimitReachedMessage: 'Сиз сүрөт иштетүү чегине жеттиңиз. Улантуу үчүн көбүрөөк сүрөт сатып алыңыз.',
      loadingImageUsageData: 'Сүрөт колдонуу маалыматтары жүктөлүүдө...',

      // Status Messages
      hello: 'Салам',
      processing: 'Иштетүү...',
      recording: 'Жазуу...',
      translating: 'Которуу...',
      error: 'Ката',
      success: 'Ийгилик',

      // Success Messages
      wordsAddedSuccessfully: 'Сиздин эсебиңизге {0} сөз ийгиликтүү кошулду!',
      imagesAddedSuccessfully: 'Сиздин эсебиңизге {0} сүрөт ийгиликтүү кошулду!',
      textExtractedSuccessfully: 'Сүрөттөн текст ийгиликтүү чыгарылды!',
      audioGeneratedSuccessfully: 'Аудио ийгиликтүү түзүлдү!',
      originalAudioGeneratedSuccessfully: 'Оригиналдык аудио ийгиликтүү түзүлдү!',
      usageStatsResetSuccessfully: 'Колдонуу статистикасы ийгиликтүү нөлдөлдү.',
      purchaseSuccessful: 'Сатып алуу ийгиликтүү!',
      paymentSuccessful: 'Төлөм ийгиликтүү!',

      // Error Messages
      wordLimitExceeded: 'Сөз чеги ашып кетти',
      wordLimitExceededTitle: 'Сөз чеги ашып кетти',
      wordLimitExceededMessage: 'Сөз чеги ашып кетти. Улантуу үчүн дагы сөз сатып алыңыз.',
      wordLimitExceededDetail: 'Сөз чеги ашып кетти! Сиз {1} сөздүн ичинен {0} сөздү колдондуңуз. Улантуу үчүн дагы сөз сатып алыңыз.',
      textExceedsWordLimit: 'Текст 200 сөздүк чегинен ашып кетти. Билдирүүңүздү кыскартыңыз.',
      pleaseBuyMoreWords: 'Көбүрөөк сөз сатып алыңыз',
      pleaseBuyMoreWordsToContinue: 'Улантуу үчүн көбүрөөк сөз сатып алыңыз.',
      purchaseFailed: 'Сатып алуу ишке ашкан жок',
      purchaseFailedTryAgain: 'Сатып алуу ишке ашкан жок. Кайра аракет кылыңыз.',
      readImageFailed: 'Сүрөттөн текстти окуу мүмкүн болбоду',
      noTextFoundInImage: 'Сүрөттө текст табылган жок',
      translationFailed: 'Которуу ишке ашкан жок',
      translationFailedTryAgain: 'Которуу ишке ашкан жок. Кайра аракет кылыңыз.',
      wordsRemainingWarning: 'Эскертүү: Сизде {0} сөз гана калды ({1}% колдонулду). Жакында көбүрөөк сөз сатып алууну сунуштайбыз.',
      approachingWordLimit: 'Сөз чегине жакындап жатат',

      // Continuous Conversation Modal
      continuousConversation: 'Узактыкбайланыш',
      bidirectionalConversation: 'Эки багыттуу байланыш',
      readyToTranscribe: 'Транскрипцияга даяр',
      transcribing: 'Транскрипция...',
      startTranscribing: 'Транскрипцияны баштоо',
      stopTranscribing: 'Транскрипцияны токтотуу',
      transcripts: 'Транскрипциялар',
      conversation: 'Сүйлөшүү',
      noTranscriptsYet: 'Транскрипциялар жок. Баштоо үчүн транскрипцияны баштаңыз!',
      buyMoreWords: 'Көбүрөөк сөз сатып алуу',
      dismiss: 'Жабуу',
      lectureMode: 'Лекция режими',
      lectureModeDescription: 'Жакшы котормо контексти үчүн ар 20 секунд сайын жазуу',
      advancedModel: 'Өркүндөтүлгөн котормо модели',
      advancedModelDescription: 'gpt-5.4-mini колдонот. Жайыраак болушу мүмкүн.',
      liveTranslation: "Түз котормо",
      liveTranslationDescription: "Сүйлөп жатканда котормолойт (gpt-realtime-translate): Үзгүлтүксүз сүйлөшүү жана Түз берүү режимдеринде. Таанылган тилдер: англис, корей, кытай (мандарин), орус, казак, өзбек, фарсы, испан. Үн менен котормо: англис, корей, кытай (мандарин), орус, испан.",
      liveTranslationFallback: "Түз котормо {0} → {1} колдобойт. Кадимки котормо колдонулат.",
      liveTranslationNotSupported: "түз котормодо жок",

      // Broadcast Translation Modal
      broadcastTranslation: 'Котормо трансляция',
      speaker: 'Сүйлөөчү',
      listener: 'Угуучу',
      sourceLanguage: 'Баштапкы тил',
      sourceLanguageSet: 'Баштапкы тил сиздин бөлүмдөн орнотулган',
      targetLanguage: 'Керектүү тил',
      addLanguage: 'Тил кошуу',
      broadcastCode: 'Трансляция коду',
      shareCode: 'Бул кодду угуучулар менен бөлүшүңүз',
      startBroadcast: 'Трансляцияны баштоо',
      stopBroadcast: 'Трансляцияны токтотуу',
      enterCode: 'Трансляция кодуну киргизиңиз',
      selectLanguage: 'Тилди тандаңыз',
      joinBroadcast: 'Трансляцияга кошулуу',
      listeningTo: 'Угууда',
      leaveBroadcast: 'Трансляциядан чыгуу',
      messages: 'Билдирүүлөр',
      waitingForMessages: 'Билдирүүлөрдү күтүүдө...',
      availableLanguages: 'Жеткиликтүү тилдер',
      conversationTopic: 'Сүйлөшүү темасы (милдеттүү эмес)',
      topicPlaceholder: 'Мисалы: Медициналык кеңеш, Бизнес жолугушуу, Саякатты пландоо...',
      topicHint: 'Теманы кошуу которуунун тактыгын жакшыртат',
      audioOn: 'Аудио: КУЧАТУУ',
      audioOff: 'Аудио: ӨЧҮРҮЛДҮ',
      speaking: 'Сүйлөп жатат...',
      wordCountWarning: 'Эскертүү: Ар бир кошумча тил сөздөрдүн санын көбөйтөт (мисалы, 2 тил = 2 эсе, 3 тил = 3 эсе)',

      // Auth0 Error Messages
      auth0LoginFailed: 'Auth0 кирүү ийгиликсиз болду',
      auth0LoginTimedOut: 'Auth0 кирүү мөөнөтү бүттү',
      auth0RegistrationFailed: 'Auth0 катталуу ийгиликсиз болду',
      auth0RegistrationTimedOut: 'Auth0 катталуу мөөнөтү бүттү',
      invalidAuthResponse: 'Жараксыз аутентификация жообу',
      facebookLoginDenied: 'Facebook аркылуу кирүү четке кагылды',
      facebookPermissionsRequired: 'Facebook уруксаттары талап кылынат',
      loginRequired: 'Кирүү талап кылынат',
      notMobilePlatform: 'Бул функция мобилдик платформаларда гана жеткиликтүү',
      transcriptionTimeout: 'Транскрипция убактысы бүттү',
      transcriptionTimeoutTitle: 'Транскрипция убактысы бүттү',
      transcriptionTimeoutMessage: 'Транскрипция суроосу көп убакыт алды. Кайра аракет кылып жатабыз...',
      transcriptionFailed: 'Транскрипция ишке ашкан жок. Кайра аракет кылыңыз.',
      retryingTranscription: 'Транскрипцияны кайра аракет кылып жатабыз...',

      // Support Page
      supportAndFeedback: 'Колдоо жана пикир',
      supportHeaderSubtitle: 'Биз сизге жардам берүүгө даярбыз жана сиздин ой-пикириңизди угууну каалайбыз',
      supportThankYou: 'Колдонганыңыз үчүн рахмат',
      supportFeedbackInvaluable: 'Сиздин пикириңиз биз үчүн баалуу',
      supportImproveServices: 'биз которуу кызматтарын жакшыртууну улантып',
      supportMultilingualCommunication: 'жана көп тилдүү баарлашууну',
      supportQuestionsSuggestions: 'Суроолоруңуз, сунуштарыңыз болсо же',
      supportTechnicalSupport: 'техникалык жардам керек болсо,',
      supportShareExperience: 'же TilHub менен болгон тажрыйбаңызды бөлүшкүңүз келсе,',
      supportLoveToHear: 'биз сиздин ой-пикириңизди угууну каалайбыз.',
      supportGetInTouch: 'Байланышуу',
      supportContactUs: 'Пикир, суроо же колдоо үчүн биз менен төмөнкү дарегибиз аркылуу байланышыңыз:',
      supportSendEmail: 'Электрондук почта жөнөтүү',
      supportCopyEmailAddress: 'Электрондук почта дарегин көчүрүү',
      supportEmailCopied: 'Электрондук почта дареги алмаштыргычка көчүрүлдү!',
      supportBackToApp: 'Колдонмого кайтуу',
      supportDeleteAccount: 'Аккаунтуңузду жок кылуу',
      supportDeleteAccountDescription: 'Сиз каалаган убакта аккаунтуңузду жана баардык байланышкан маалыматтарды жок кылууну сурай аласыз.',
      supportRequestAccountDeletion: 'Аккаунтту жок кылууну суроо',
      supportDeleteAccountConfirmation: 'Are you sure you want to delete your account? This action cannot be undone. All your data will be permanently deleted.',
      supportDeleteAccountFinalConfirm: 'This is your final warning. Your account and all data will be permanently deleted. Are you absolutely sure?',
      supportAccountDeleted: 'Your account has been deleted successfully. You will be logged out now.',
      supportDeleteAccountError: 'Failed to delete account. Please try again or contact support.',
      supportDeletingAccount: 'Deleting...',
      support: 'Колдоо',
    },

    // Kazakh (Қазақша)
    kk: {
      // Navigation
      languages: 'Тілдер',
      languageSettings: 'Тіл баптаулары',
      voiceType: 'Дауыс түрі',
      account: 'Тіркелгі',
      profile: 'Профиль',
      settings: 'Баптаулар',
      history: 'Тарих',
      wordUsage: 'Сөз пайдалану',
      imageUsage: 'Сурет пайдалану',
      analytics: 'Аналитика',
      buyWords: 'Аударма деректерін сатып алу',
      buyImages: 'Сурет деректерін сатып алу',
      buyDataFromWebapp: 'Веб-қосымшадан деректер сатып алу',
      login: 'Кіру',
      register: 'Тіркелу',
      logout: 'Шығу',

      // Buy Modals
      buyWordsTitle: 'Сөздерді сатып алу',
      buyWordsSubtitle: 'Аудару үшін қосымша деректерді сатып алу',
      buyImagesTitle: 'Суреттерді сатып алу',
      buyImagesSubtitle: 'Сурет өңдеу үшін қосымша кредиттерді сатып алу',
      conversationTopicPlaceholder: 'Әңгіме тақырыбы (мысалы, тіс емдеу лекциясы, медициналық конференция)',
      conversationTopicHint: 'Міндетті емес: белгілі бір тақырыптар үшін аударма дәлдігін жақсартуға көмектеседі',
      selectWordPackage: 'Сөз пакетін таңдау',
      selectImagePackage: 'Сурет пакетін таңдау',
      oneTimePurchase: 'Бір реттік сатып алу',
      buyMoreSaveMore: 'Көбірек сатып алсаңыз, көбірек үнемдейсіз!',
      popular: 'Танымал',
      data: 'деректер',
      wordCredits: 'сөз кредиттері',
      imageProcessingCredits: 'сурет өңдеу кредиттері',
      perfectForTesting: 'Тестілеуге тамаша',
      save: 'Үнемдеу',
      selectAnOption: 'Опцияны таңдау',
      howItWorks: 'Қалай жұмыс істейді:',
      oneTimePurchaseAddsWords:
        'Бір реттік сатып алу сөздерді тіркелгіңізге мәңгілік қосады',
      wordsConsumedAsYouUse:
        'Аударма функцияларын пайдаланғанда сөздер тұтынылады',
      noExpirationUseWords:
        'Мерзімі жоқ - қажет болғанда сөздерді пайдаланыңыз',
      oneTimePurchaseAddsImages:
        'Бір реттік сатып алу суреттерді тіркелгіңізге мәңгілік қосады',
      imagesConsumedWhenYouUseOCR:
        'Мәтінді шығаруды пайдаланғанда суреттер тұтынылады',
      noExpirationUseImages:
        'Мерзімі жоқ - қажет болғанда суреттерді пайдаланыңыз',

      // Language Selection
      yourLanguage: 'Сіздің тіліңіз:',
      partnerLanguage: 'Серіктестің тілі:',
      speakingSpeed: 'Сөйлеу жылдамдығы:',
      slow: 'Баяу',
      fast: 'Жылдам',

      // Main App
      translation: 'Аудару',
      start: 'Бастау',
      end: 'Аяқтау',
      clear: 'Тазалау',
      clearHistory: 'Тарихты тазалау',
      showAll: 'Бәрін көрсету',
      readImage: 'Суретті оқу',
      translate: 'Аудару',
      typeMessage: 'Сіздің хабарламаңызды мұнда теру...',
      typePartnerMessage: 'Серіктестің хабарламасын мұнда теру...',
      maxWords: 'макс 200 сөз',
      words: 'сөз',
      characters: 'символ',

      // Conversation
      youSpeakLabel: 'Сіз сөйлейсіз',
      partnerSpeaksLabel: 'Серіктес сөйлейді',

      // History Modal
      conversationHistory: 'Сөйлесу тарихы',
      searchConversations: 'Сөйлесулерді іздеу...',
      allAudioFiles: 'Барлық аудио файлдар',
      clearAll: 'Бәрін тазалау',
      downloadAll: 'Барлығын жүктеп алу',
      play: 'Ойнату',
      delete: 'Жою',
      confidence: 'Сенімділік',
      recorded: 'Жазылған',
      conversations: 'сөйлесулер',
      totalDuration: 'жалпы ұзақтығы',
      noConversationsFound: 'Сөйлесулер табылмады',
      noConversationsYet: 'Әзірге сөйлесулер жоқ',
      tryAdjustingSearch: 'Іздеу шарттарын реттеп көріңіз',
      startConversationToSee: 'Көру үшін сөйлесу бастаңыз',
      youWrote: 'Сіз жаздыңыз',
      youSpoke: 'Сіз айттыңыз',
      partnerWrote: 'Серіктес жазды',
      partnerHears: 'Серіктес естиді',
      noOriginalText: 'Түпнұсқа мәтін жоқ',
      noTranslation: 'Аударма жоқ',
      download: 'Жүктеп алу',
      generateAudioFor: 'Аудио жасау ',
      generating: 'Жасалуда...',

      // Word Usage Modal
      wordUsageAndQuotas: 'Сөз пайдалану және квоталар',
      trackTranslationUsage: 'Аударма пайдалануыңызды қадағалаңыз және квоталарды басқарыңыз',
      currentUsage: 'Ағымдағы пайдалану',
      wordsUsage: 'Сөз пайдалану',
      switchToTotalView: 'Жалпы көрініске ауысу',
      switchToRemainingView: 'Қалған көрініске ауысу',
      of: '/',
      lastReset: 'Соңғы қалпына келтіру:',
      quotaSettings: 'Квота параметрлері',
      enableWordQuotas: 'Сөз квоталарын қосу',
      setLimitsOnWordUsage: 'Сөз пайдалануға шектеулер қою',
      wordLimit: 'Сөз шегі',
      maximumWordsPerResetPeriod: 'Қалпына келтіру кезеңіндегі максималды сөздер',
      resetPeriod: 'Қалпына келтіру кезеңі',
      daily: 'Күн сайын',
      weekly: 'Апта сайын',
      monthly: 'Ай сайын',
      neverManualResetOnly: 'Ешқашан (тек қолмен қалпына келтіру)',
      howOftenUsageStatsReset: 'Пайдалану статистикасы қанша уақытта қалпына келтіріледі',

      // Image Usage Modal
      trackImageProcessingUsage: 'Сурет өңдеу пайдалануыңызды бақылаңыз',
      imagesUsage: 'Суреттер пайдалануы',
      nearLimit: 'Шекке жақын',
      warning: 'Ескерту',
      good: 'Жақсы',
      imagesUsed: 'Пайдаланылған суреттер',
      remaining: 'Қалған',
      imagesRemaining: 'сурет қалды',
      imagesUsedText: 'сурет пайдаланылды',
      imagesAvailable: 'сурет қолжетімді',
      statistics: 'Статистика',
      totalImagesProcessed: 'Жалпы өңделген суреттер:',
      lastActive: 'Соңғы белсенділік:',
      never: 'Ешқашан',
      accountCreated: 'Аккаунт құрылған:',
      imageLimitReached: 'Сурет шегіне жетті',
      imageLimitReachedMessage: 'Сіз сурет өңдеу шегіне жеттіңіз. Жалғастыру үшін көбірек сурет сатып алыңыз.',
      loadingImageUsageData: 'Сурет пайдалану деректері жүктелуде...',

      // Status Messages
      hello: 'Сәлем',
      processing: 'Өңдеу...',
      recording: 'Жазу...',
      translating: 'Аудару...',
      error: 'Қате',
      success: 'Сәтті',

      // Success Messages (using English as placeholder - add translations later)
      wordsAddedSuccessfully: 'Successfully added {0} words to your account!',
      imagesAddedSuccessfully: 'Successfully added {0} images to your account!',
      textExtractedSuccessfully: 'Text extracted from image successfully!',
      audioGeneratedSuccessfully: 'Audio generated successfully!',
      originalAudioGeneratedSuccessfully: 'Original audio generated successfully!',
      usageStatsResetSuccessfully: 'Usage statistics have been reset successfully.',
      purchaseSuccessful: 'Purchase successful!',
      paymentSuccessful: 'Payment successful!',

      // Error Messages (using English as placeholder - add translations later)
      wordLimitExceeded: 'Word limit exceeded',
      wordLimitExceededTitle: 'Word Limit Exceeded',
      wordLimitExceededMessage: 'Word limit exceeded. Please buy more words to continue.',
      wordLimitExceededDetail: "You've used {0} out of {1} words. Please buy more words to continue.",
      textExceedsWordLimit: 'Text exceeds 200 words limit. Please shorten your message.',
      pleaseBuyMoreWords: 'Please buy more words',
      pleaseBuyMoreWordsToContinue: 'Please buy more words to continue.',
      purchaseFailed: 'Failed to purchase',
      purchaseFailedTryAgain: 'Failed to purchase. Please try again.',
      readImageFailed: 'Failed to read text from image',
      noTextFoundInImage: 'No text found in the image',
      translationFailed: 'Translation failed',
      translationFailedTryAgain: 'Translation failed. Please try again.',
      wordsRemainingWarning: 'Warning: You have only {0} words remaining ({1}% used). Consider buying more words soon.',
      approachingWordLimit: 'Approaching word limit',

      // Continuous Conversation Modal
      continuousConversation: 'Үзіліссіз сұхбат',
      bidirectionalConversation: 'Екі бағытты сұхбат',
      readyToTranscribe: 'Транскрипцияға дайын',
      transcribing: 'Транскрипция...',
      startTranscribing: 'Транскрипцияны бастау',
      stopTranscribing: 'Транскрипцияны тоқтату',
      transcripts: 'Транскрипциялар',
      conversation: 'Сұхбат',
      noTranscriptsYet: 'Транскрипциялар жоқ. Бастау үшін транскрипцияны бастаңыз!',
      buyMoreWords: 'Buy More Words',
      dismiss: 'Dismiss',
      lectureMode: 'Лекция режимі',
      lectureModeDescription: 'Жақсы аударма контексті үшін әр 20 секунд сайын жазу',
      advancedModel: 'Жетілдірілген аударма моделі',
      advancedModelDescription: 'gpt-5.4-mini пайдаланады. Баяуырақ болуы мүмкін.',
      liveTranslation: "Ілеспе аударма",
      liveTranslationDescription: "Сөйлеп жатқанда аударады (gpt-realtime-translate): Үздіксіз сөйлесу және Трансляция режимдерінде. Танитын тілдер: ағылшын, корей, қытай (мандарин), орыс, қазақ, өзбек, парсы, испан. Дауыспен аударма: ағылшын, корей, қытай (мандарин), орыс, испан.",
      liveTranslationFallback: "Ілеспе аударма {0} → {1} қолдамайды. Қалыпты аударма қолданылады.",
      liveTranslationNotSupported: "ілеспе аудармада жоқ",

      // Broadcast Translation Modal
      broadcastTranslation: 'Трансляция аудармасы',
      speaker: 'Сөйлеуші',
      listener: 'Тыңдаушы',
      sourceLanguage: 'Бастапқы тіл',
      sourceLanguageSet: 'Бастапқы тіл сіздің бөліміңізден орнатылған',
      targetLanguage: 'Мақсатты тіл',
      addLanguage: 'Тіл қосу',
      broadcastCode: 'Трансляция коды',
      shareCode: 'Бұл кодты тыңдаушылармен бөлісіңіз',
      startBroadcast: 'Трансляцияны бастау',
      stopBroadcast: 'Трансляцияны тоқтату',
      enterCode: 'Трансляция кодын енгізіңіз',
      selectLanguage: 'Тілді таңдаңыз',
      joinBroadcast: 'Трансляцияға қосылу',
      listeningTo: 'Тыңдауда',
      leaveBroadcast: 'Трансляциядан шығу',
      messages: 'Хабарламалар',
      waitingForMessages: 'Хабарламаларды күтуде...',
      availableLanguages: 'Қолжетімді тілдер',
      conversationTopic: 'Әңгіме тақырыбы (міндетті емес)',
      topicPlaceholder: 'Мысалы: Медициналық кеңес, Бизнес кездесуі, Саяхатты жоспарлау...',
      topicHint: 'Тақырыпты қосу аударманың дәлдігін жақсартады',
      audioOn: 'Аудио: ҚОСУЛЫ',
      audioOff: 'Аудио: ӨШІРІЛГЕН',
      speaking: 'Сөйлеп жатыр...',
      wordCountWarning: 'Ескерту: Әр қосымша тіл сөздер санын көбейтеді (мысалы, 2 тіл = 2 есе, 3 тіл = 3 есе)',

      // Auth0 Error Messages
      auth0LoginFailed: 'Auth0 кіру сәтсіз болды',
      auth0LoginTimedOut: 'Auth0 кіру уақыты аяқталды',
      auth0RegistrationFailed: 'Auth0 тіркелу сәтсіз болды',
      auth0RegistrationTimedOut: 'Auth0 тіркелу уақыты аяқталды',
      invalidAuthResponse: 'Жарамсыз аутентификация жауабы',
      facebookLoginDenied: 'Facebook арқылы кіру қабылданбады',
      facebookPermissionsRequired: 'Facebook рұқсаттары қажет',
      loginRequired: 'Кіру қажет',
      notMobilePlatform: 'Бұл функция тек мобильді платформаларда қолжетімді',
      transcriptionTimeout: 'Транскрипция уақыты аяқталды',
      transcriptionTimeoutTitle: 'Транскрипция уақыты аяқталды',
      transcriptionTimeoutMessage: 'Транскрипция сұрауы көп уақыт алды. Қайта әрекет жасап жатырмыз...',
      transcriptionFailed: 'Транскрипция сәтсіз аяқталды. Қайта әрекет жасаңыз.',
      retryingTranscription: 'Транскрипцияны қайта әрекет жасап жатырмыз...',

      // Support Page
      supportAndFeedback: 'Қолдау және пікір',
      supportHeaderSubtitle: 'Біз сізге көмектесуге дайынбыз және сіздің пікіріңізді тыңдағымдыз келеді',
      supportThankYou: 'Пайдаланғаныңызға рахмет',
      supportFeedbackInvaluable: 'Сіздің пікіріңіз біз үшін құнды',
      supportImproveServices: 'біз аударма қызметтерін жақсартуды жалғастырып',
      supportMultilingualCommunication: 'және көптілді байланысты',
      supportQuestionsSuggestions: 'Сұрақтарыңыз, ұсыныстарыңыз болса немесе',
      supportTechnicalSupport: 'техникалық қолдау қажет болса,',
      supportShareExperience: 'немесе TilHub-пен болған тәжірибеңізді бөлісқыңыз келсе,',
      supportLoveToHear: 'біз сіздің пікіріңізді тыңдағымдыз келеді.',
      supportGetInTouch: 'Байланысу',
      supportContactUs: 'Пікір, сұрақ немесе қолдау үшін бізбен төмендегі мекен-жайымыз арқылы байланысыңыз:',
      supportSendEmail: 'Электрондық пошта жіберу',
      supportCopyEmailAddress: 'Электрондық пошта мекенжайын көшіру',
      supportEmailCopied: 'Электрондық пошта мекенжайы алмасу буферіне көшірілді!',
      supportBackToApp: 'Қолданбаға оралу',
      supportDeleteAccount: 'Есептік жазбаңызды жою',
      supportDeleteAccountDescription: 'Сіз кез келген уақытта есептік жазбаңызды және барлық байланысты деректерді жоюды сұрай аласыз.',
      supportRequestAccountDeletion: 'Есептік жазбаны жоюды сұрау',
      supportDeleteAccountConfirmation: 'Are you sure you want to delete your account? This action cannot be undone. All your data will be permanently deleted.',
      supportDeleteAccountFinalConfirm: 'This is your final warning. Your account and all data will be permanently deleted. Are you absolutely sure?',
      supportAccountDeleted: 'Your account has been deleted successfully. You will be logged out now.',
      supportDeleteAccountError: 'Failed to delete account. Please try again or contact support.',
      supportDeletingAccount: 'Deleting...',
      support: 'Қолдау',
    },

    // Tajik (Тоҷикӣ)
    tg: {
      // Navigation
      languages: 'Забонҳо',
      languageSettings: 'Танзимоти забон',
      voiceType: 'Навъи овоз',
      account: 'Ҳисоб',
      profile: 'Профил',
      settings: 'Танзимот',
      history: 'Таърих',
      wordUsage: 'Истифодаи калима',
      imageUsage: 'Истифодаи тасвир',
      analytics: 'Аналитика',
      buyWords: 'Харидани маълумоти тарҷума',
      buyImages: 'Харидани маълумоти тасвир',
      buyDataFromWebapp: 'Маълумотро аз барномаи вебӣ харидан',
      login: 'Ворид шудан',
      register: 'Сабт шудан',
      logout: 'Баромадан',

      // Buy Modals
      buyWordsTitle: 'Харидани калимаҳо',
      buyWordsSubtitle: 'Харидани маълумоти иловагии тарҷума',
      buyImagesTitle: 'Харидани тасвирҳо',
      buyImagesSubtitle: 'Харидани кредитҳои иловагии коркарди тасвир',
      conversationTopicPlaceholder: 'Мавзӯи суҳбат (масалан, лексияи дандонпизишкӣ, конфронси тиббӣ)',
      conversationTopicHint: 'Ихтиёрӣ: барои мавзӯъҳои муайян дақиқии тарҷумаро беҳтар мекунад',
      selectWordPackage: 'Интихоби пакети калимаҳо',
      selectImagePackage: 'Интихоби пакети тасвирҳо',
      oneTimePurchase: 'Харидани яккара',
      buyMoreSaveMore: 'Зиёдтар харед, зиёдтар захира кунед!',
      popular: 'Машҳур',
      data: 'маълумот',
      wordCredits: 'кредитҳои калима',
      imageProcessingCredits: 'кредитҳои коркарди тасвир',
      perfectForTesting: 'Барои санҷиш беҳтарин',
      save: 'Захира',
      selectAnOption: 'Интихоби вариант',
      howItWorks: 'Чӣ тавр кор мекунад:',
      oneTimePurchaseAddsWords:
        'Харидани яккара калимаҳоро ба ҳисоби шумо ҳамеша меафзояд',
      wordsConsumedAsYouUse:
        'Калимаҳо ҳангоми истифодаи хусусиятҳои тарҷума сарп мешаванд',
      noExpirationUseWords:
        'Муҳлати анҷом нест - калимаҳоро ҳар вақт ки лозим аст истифода баред',
      oneTimePurchaseAddsImages:
        'Харидани яккара тасвирҳоро ба ҳисоби шумо ҳамеша меафзояд',
      imagesConsumedWhenYouUseOCR:
        'Тасвирҳо ҳангоми истифодаи баровардани матн сарп мешаванд',
      noExpirationUseImages:
        'Муҳлати анҷом нест - тасвирҳоро ҳар вақт ки лозим аст истифода баред',

      // Language Selection
      yourLanguage: 'Забони шумо:',
      partnerLanguage: 'Забони ҳамроҳ:',
      speakingSpeed: 'Суръати сухан:',
      slow: 'Оҳиста',
      fast: 'Тез',

      // Main App
      translation: 'Тарҷума',
      start: 'Оғоз',
      end: 'Хотима',
      clear: 'Тоза кардан',
      clearHistory: 'Тоза кардани таърих',
      showAll: 'Нишон додани ҳама',
      readImage: 'Хондани тасвир',
      translate: 'Тарҷума кардан',
      typeMessage: 'Пайоматро дар ин ҷо нависед...',
      typePartnerMessage: 'Пайомати ҳамроҳро дар ин ҷо нависед...',
      maxWords: 'макс 200 калима',
      words: 'калима',
      characters: 'рамз',

      // Conversation
      youSpeakLabel: 'Шумо мегӯед',
      partnerSpeaksLabel: 'Ҳамроҳ мегӯяд',

      // History Modal
      conversationHistory: 'Таърихи муколама',
      searchConversations: 'Ҷустуҷӯи муколамаҳо...',
      allAudioFiles: 'Ҳамаи файлҳои аудио',
      clearAll: 'Тоза кардани ҳама',
      downloadAll: 'Боргирии ҳама',
      play: 'Пахш кардан',
      delete: 'Ҳазф кардан',
      confidence: 'Эътимод',
      recorded: 'Сабтшуда',
      conversations: 'муколамаҳо',
      totalDuration: 'давомнокии умумӣ',
      noConversationsFound: 'Муколамаҳо ёфт нашуданд',
      noConversationsYet: 'Ҳанӯз муколамаҳо нестанд',
      tryAdjustingSearch: 'Шартҳои ҷустуҷӯро танзим кунед',
      startConversationToSee: 'Барои дидан муколамае оғоз кунед',
      youWrote: 'Шумо навиштед',
      youSpoke: 'Шумо гуфтед',
      partnerWrote: 'Шарик навишт',
      partnerHears: 'Шарик мешунавад',
      noOriginalText: 'Матни аслӣ нест',
      noTranslation: 'Тарҷума нест',
      download: 'Боргирӣ кардан',
      generateAudioFor: 'Эҷоди аудио барои ',
      generating: 'Эҷод...',

      // Word Usage Modal
      wordUsageAndQuotas: 'Истифодаи калимаҳо ва квотаҳо',
      trackTranslationUsage: 'Истифодаи тарҷумаи худро назорат кунед ва квотаҳоро идора намоед',
      currentUsage: 'Истифодаи ҷорӣ',
      wordsUsage: 'Истифодаи калимаҳо',
      switchToTotalView: 'Гузариш ба намоиши умумӣ',
      switchToRemainingView: 'Гузариш ба намоиши боқимонда',
      of: 'аз',
      lastReset: 'Таҳлили охирин:',
      quotaSettings: 'Танзимоти квота',
      enableWordQuotas: 'Фаъол кардани квотаҳои калимаҳо',
      setLimitsOnWordUsage: 'Гузоштани маҳдудиятҳо барои истифодаи калимаҳо',
      wordLimit: 'Маҳдудияти калимаҳо',
      maximumWordsPerResetPeriod: 'Калимаҳои максималӣ барои давраи таҳлил',
      resetPeriod: 'Давраи таҳлил',
      daily: 'Ҳаррӯза',
      weekly: 'Ҳафтавор',
      monthly: 'Моҳвор',
      neverManualResetOnly: 'Ҳеҷ вақт (танҳо таҳлили дастӣ)',
      howOftenUsageStatsReset: 'Омори истифода чӣ хеле таҳлил мешавад',

      // Image Usage Modal
      trackImageProcessingUsage: 'Истифодаи коркарди тасвирро пайгирӣ кунед',
      imagesUsage: 'Истифодаи тасвирҳо',
      nearLimit: 'Наздик ба ҳад',
      warning: 'Огоҳӣ',
      good: 'Хуб',
      imagesUsed: 'Тасвирҳои истифодашуда',
      remaining: 'Монда',
      imagesRemaining: 'тасвир мондааст',
      imagesUsedText: 'тасвир истифода шуд',
      imagesAvailable: 'тасвир дастрас аст',
      statistics: 'Омор',
      totalImagesProcessed: 'Ҳамаи тасвирҳои коркардшуда:',
      lastActive: 'Фаъолияти охирин:',
      never: 'Ҳеҷ гоҳ',
      accountCreated: 'Ҳисоб эҷод шуд:',
      imageLimitReached: 'Ҳади тасвир расид',
      imageLimitReachedMessage: 'Шумо ба ҳади коркарди тасвир расидед. Барои идома додан тасвирҳои бештар харед.',
      loadingImageUsageData: 'Додаҳои истифодаи тасвир бор мешаванд...',

      // Status Messages
      hello: 'Салом',
      processing: 'Коркарӣ...',
      recording: 'Сабт...',
      translating: 'Тарҷума...',
      error: 'Хатогӣ',
      success: 'Муваффақият',

      // Success Messages (using English as placeholder - add translations later)
      wordsAddedSuccessfully: 'Successfully added {0} words to your account!',
      imagesAddedSuccessfully: 'Successfully added {0} images to your account!',
      textExtractedSuccessfully: 'Text extracted from image successfully!',
      audioGeneratedSuccessfully: 'Audio generated successfully!',
      originalAudioGeneratedSuccessfully: 'Original audio generated successfully!',
      usageStatsResetSuccessfully: 'Usage statistics have been reset successfully.',
      purchaseSuccessful: 'Purchase successful!',
      paymentSuccessful: 'Payment successful!',

      // Error Messages (using English as placeholder - add translations later)
      wordLimitExceeded: 'Word limit exceeded',
      wordLimitExceededTitle: 'Word Limit Exceeded',
      wordLimitExceededMessage: 'Word limit exceeded. Please buy more words to continue.',
      wordLimitExceededDetail: "You've used {0} out of {1} words. Please buy more words to continue.",
      textExceedsWordLimit: 'Text exceeds 200 words limit. Please shorten your message.',
      pleaseBuyMoreWords: 'Please buy more words',
      pleaseBuyMoreWordsToContinue: 'Please buy more words to continue.',
      purchaseFailed: 'Failed to purchase',
      purchaseFailedTryAgain: 'Failed to purchase. Please try again.',
      readImageFailed: 'Failed to read text from image',
      noTextFoundInImage: 'No text found in the image',
      translationFailed: 'Translation failed',
      translationFailedTryAgain: 'Translation failed. Please try again.',
      wordsRemainingWarning: 'Warning: You have only {0} words remaining ({1}% used). Consider buying more words soon.',
      approachingWordLimit: 'Approaching word limit',

      // Continuous Conversation Modal
      continuousConversation: 'Муомилаи пайваста',
      bidirectionalConversation: 'Муомилаи дутарафа',
      readyToTranscribe: 'Барои транскрипсия омода',
      transcribing: 'Транскрипсия...',
      startTranscribing: 'Транскрипсияро оғоз кардан',
      stopTranscribing: 'Транскрипсияро қатъ кардан',
      transcripts: 'Транскрипсияҳо',
      conversation: 'Муомила',
      noTranscriptsYet: 'Транскрипсияҳо ҳанӯз нестанд. Оғоз кардан барои транскрипсияро оғоз кунед!',
      buyMoreWords: 'Buy More Words',
      dismiss: 'Dismiss',
      lectureMode: 'Реҷаи лексия',
      lectureModeDescription: 'Барои контексти беҳтари тарҷума ҳар 20 сония сабт кардан',
      advancedModel: 'Модели пешрафтаи тарҷума',
      advancedModelDescription: 'gpt-5.4-mini-ро истифода мебарад. Метавонад сусттар бошад.',
      liveTranslation: "Тарҷумаи ҳамзамон",
      liveTranslationDescription: "Ҳангоми сухан тарҷума мекунад (gpt-realtime-translate): дар Сӯҳбати пайваста ва Пахш. Забонҳои шинохташаванда: англисӣ, кореягӣ, хитоӣ (мандарин), русӣ, қазоқӣ, ӯзбекӣ, форсӣ, испанӣ. Тарҷума бо овоз: англисӣ, кореягӣ, хитоӣ (мандарин), русӣ, испанӣ.",
      liveTranslationFallback: "Тарҷумаи ҳамзамон {0} → {1}-ро дастгирӣ намекунад. Тарҷумаи оддӣ истифода мешавад.",
      liveTranslationNotSupported: "дар тарҷумаи ҳамзамон дастрас нест",

      // Broadcast Translation Modal
      broadcastTranslation: 'Тарҷумаи пахш',
      speaker: 'Сухангӯ',
      listener: 'Гӯшкунанда',
      sourceLanguage: 'Забони манбаъ',
      sourceLanguageSet: 'Забони манбаъ аз қисми шумо таъин шудааст',
      targetLanguage: 'Забони мақсад',
      addLanguage: 'Забон илова кардан',
      broadcastCode: 'Коди пахш',
      shareCode: 'Ин кодро бо гӯшкунандагон мубодила кунед',
      startBroadcast: 'Пахшро оғоз кардан',
      stopBroadcast: 'Пахшро бекор кардан',
      enterCode: 'Коди пахшро ворид кунед',
      selectLanguage: 'Забонро интихоб кунед',
      joinBroadcast: 'Ба пахш пайванд шудан',
      listeningTo: 'Гӯш додан',
      leaveBroadcast: 'Пахшро тарк кардан',
      messages: 'Пайомҳо',
      waitingForMessages: 'Интизории пайомҳо...',
      availableLanguages: 'Забонҳои дастрас',
      conversationTopic: 'Мавзӯи муколама (ихтиёрӣ)',
      topicPlaceholder: 'Масалан: Машварати тиббӣ, Ҷаласаи тиҷорат, Барномаи сафар...',
      topicHint: 'Илова кардани мавзӯ ба беҳтар кардани дақиқии тарҷума кӯмак мекунад',
      audioOn: 'Аудио: ФАЪЛ',
      audioOff: 'Аудио: ҒАЙРИФАЪЛ',
      speaking: 'Сухан мегӯяд...',
      wordCountWarning: 'Эзоҳ: Ҳар як забони иловагӣ шумораи калимаҳоро зиёд мекунад (масалан, 2 забон = 2 маротиба, 3 забон = 3 маротиба)',

      // Auth0 Error Messages
      auth0LoginFailed: 'Даромадан ба Auth0 ноком шуд',
      auth0LoginTimedOut: 'Мӯҳлати даромадан ба Auth0 ба поён расид',
      auth0RegistrationFailed: 'Сабт шудан ба Auth0 ноком шуд',
      auth0RegistrationTimedOut: 'Мӯҳлати сабт шудан ба Auth0 ба поён расид',
      invalidAuthResponse: 'Ҷавоби аутентификатсияи нодуруст',
      facebookLoginDenied: 'Даромадан ба Facebook рад карда шуд',
      facebookPermissionsRequired: 'Иҷозатҳои Facebook талаб карда мешаванд',
      loginRequired: 'Даромадан талаб карда мешавад',
      notMobilePlatform: 'Ин функсия танҳо дар платформаҳои мобилӣ дастрас аст',
      transcriptionTimeout: 'Вақти транскриптсия тамом шуд',
      transcriptionTimeoutTitle: 'Вақти транскриптсия тамом шуд',
      transcriptionTimeoutMessage: 'Дархости транскриптсия зиёд вақт гирифт. Такрор карда истодаем...',
      transcriptionFailed: 'Транскриптсия ноком шуд. Лутфан, такрор кунed.',
      retryingTranscription: 'Транскриптсияро такрор карда истодаем...',

      // Support Page
      supportAndFeedback: 'Дастгирӣ ва фикр',
      supportHeaderSubtitle: 'Мо дар ин ҷо ҳастем, то ба шумо кӯмак кунем ва мехоҳем фикри шуморо бишнавем',
      supportThankYou: 'Барои истифода кардани',
      supportFeedbackInvaluable: 'Фикри шумо барои мо қадрнок аст',
      supportImproveServices: 'мо хизматрасонии тарҷумаро беҳтар карда идома медиҳем',
      supportMultilingualCommunication: 'ва муоширати бисёрзабонро',
      supportQuestionsSuggestions: 'Агар саволҳо, пешниҳодҳо дошта бошед,',
      supportTechnicalSupport: 'кӯмаки техникӣ лозим бошад,',
      supportShareExperience: 'ё мехоҳед таҷрибаи худро бо TilHub мубодила кунед,',
      supportLoveToHear: 'мо мехоҳем фикри шуморо бишнавем.',
      supportGetInTouch: 'Бо мо тамос гиред',
      supportContactUs: 'Барои фикр, савол ё дастгирӣ, лутфан бо мо тавассути суроғаи зерин тамос гиред:',
      supportSendEmail: 'Имейл фиристодан',
      supportCopyEmailAddress: 'Нусха бардоштани суроғаи имейл',
      supportEmailCopied: 'Суроғаи имейл ба ҳофизаи муваққатӣ нусха бардошта шуд!',
      supportBackToApp: 'Бозгашт ба барнома',
      supportDeleteAccount: 'Ҳисобро нест кардан',
      supportDeleteAccountDescription: 'Шумо дар ҳар вақт метавонед ҳисоб ва ҳамаи додаҳои алоқамандро нест карданро дархост кунед.',
      supportRequestAccountDeletion: 'Дархости нест кардани ҳисоб',
      supportDeleteAccountConfirmation: 'Are you sure you want to delete your account? This action cannot be undone. All your data will be permanently deleted.',
      supportDeleteAccountFinalConfirm: 'This is your final warning. Your account and all data will be permanently deleted. Are you absolutely sure?',
      supportAccountDeleted: 'Your account has been deleted successfully. You will be logged out now.',
      supportDeleteAccountError: 'Failed to delete account. Please try again or contact support.',
      supportDeletingAccount: 'Deleting...',
      support: 'Дастгирӣ',
    },

    // Turkmen (Türkmençe)
    tk: {
      // Navigation
      languages: 'Diller',
      languageSettings: 'Dil sazlamalary',
      voiceType: 'Ses görnüşi',
      account: 'Hesap',
      profile: 'Profil',
      settings: 'Sazlamalar',
      history: 'Taryh',
      wordUsage: 'Söz ulanyşy',
      imageUsage: 'Surat ulanyşy',
      analytics: 'Analitika',
      buyWords: 'Terjime maglumatyny satyn almak',
      buyImages: 'Surat maglumatyny satyn almak',
      buyDataFromWebapp: 'Web-ilowadan maglumat satyn almak',
      login: 'Girmek',
      register: 'Hasaba almak',
      logout: 'Çykmak',

      // Buy Modals
      buyWordsTitle: 'Sözleri satyn almak',
      buyWordsSubtitle: 'Terjime üçin goşmaça maglumat satyn almak',
      buyImagesTitle: 'Suratlary satyn almak',
      buyImagesSubtitle: 'Surat işlemek üçin goşmaça kredit satyn almak',
      conversationTopicPlaceholder: 'Söhbet mowzugy (mysal üçin, diş lukmany leksiýasy, medisina konferensiýasy)',
      conversationTopicHint: 'Meýletin: kesgitlenen mowzuglar üçin terjime takylygyny gowulaşdyrýar',
      selectWordPackage: 'Söz paketini saýlamak',
      selectImagePackage: 'Surat paketini saýlamak',
      oneTimePurchase: 'Bir gezek satyn almak',
      buyMoreSaveMore: 'Köp satyn alsaňyz, köp tygşytlaýarsyňyz!',
      popular: 'Meşhur',
      data: 'maglumat',
      wordCredits: 'söz kreditleri',
      imageProcessingCredits: 'surat işleme kreditleri',
      perfectForTesting: 'Test üçin ajaýyp',
      save: 'Tygşytlaýyş',
      selectAnOption: 'Warianty saýlamak',
      howItWorks: 'Nähili işleýär:',
      oneTimePurchaseAddsWords:
        'Bir gezek satyn almak sözleri hasabyňyza hemişelik goşýar',
      wordsConsumedAsYouUse:
        'Terjime funksiýalaryny ulananda sözler sarp edilýär',
      noExpirationUseWords: 'Möhleti ýok - sözleri gerek bolanda ulanyň',
      oneTimePurchaseAddsImages:
        'Bir gezek satyn almak suratlary hasabyňyza hemişelik goşýar',
      imagesConsumedWhenYouUseOCR:
        'Tekst çykaryşyny ulananda suratlar sarp edilýär',
      noExpirationUseImages: 'Möhleti ýok - suratlary gerek bolanda ulanyň',

      // Language Selection
      yourLanguage: 'Siziň diliňiz:',
      partnerLanguage: 'Ýoldaşyň dili:',
      speakingSpeed: 'Gepleme tizligi:',
      slow: 'Ýawaş',
      fast: 'Tiz',

      // Main App
      translation: 'Terjime',
      start: 'Başlamak',
      end: 'Tamamlamak',
      clear: 'Arassalamak',
      clearHistory: 'Taryhy arassalamak',
      showAll: 'Hemmesini görkezmek',
      readImage: 'Suraty okamak',
      translate: 'Terjime etmek',
      typeMessage: 'Habarňyzy şu ýere ýazyň...',
      typePartnerMessage: 'Ýoldaşyň habaryny şu ýere ýazyň...',
      maxWords: 'maks 200 söz',
      words: 'söz',
      characters: 'simwol',

      // Conversation
      youSpeakLabel: 'Siz gepleýärsiňiz',
      partnerSpeaksLabel: 'Ýoldaş gepleýär',

      // History Modal
      conversationHistory: 'Gepleşik taryhy',
      searchConversations: 'Gepleşikleri gözlemek...',
      allAudioFiles: 'Ähli audio faýllar',
      clearAll: 'Hemmesini arassalamak',
      downloadAll: 'Hemmesini ýüklemek',
      play: 'Oýnatmak',
      delete: 'Pozmak',
      confidence: 'Ynanç',
      recorded: 'Ýazylan',
      conversations: 'gepleşikler',
      totalDuration: 'umumy dowamlylygy',
      noConversationsFound: 'Gepleşikler tapylmady',
      noConversationsYet: 'Entek gepleşikler ýok',
      tryAdjustingSearch: 'Gözleg şertlerini sazlaň',
      startConversationToSee: 'Görmek üçün gepleşik başlaň',
      youWrote: 'Siz ýazdyňyz',
      youSpoke: 'Siz aýtdyňyz',
      partnerWrote: 'Hyzmatdaş ýazdy',
      partnerHears: 'Hyzmatdaş eşidýär',
      noOriginalText: 'Asyl tekst ýok',
      noTranslation: 'Terjime ýok',
      download: 'Ýüklemek',
      generateAudioFor: 'Audio döretmek ',
      generating: 'Döredilýär...',

      // Word Usage Modal
      wordUsageAndQuotas: 'Söz ulanyşy we kwotalar',
      trackTranslationUsage: 'Terjime ulanyşyňyzy yzarlaň we kwotalary dolandyryň',
      currentUsage: 'Häzirki ulanyş',
      wordsUsage: 'Söz ulanyşy',
      switchToTotalView: 'Umumy görnüşe geçmek',
      switchToRemainingView: 'Galan görnüşe geçmek',
      of: '/',
      lastReset: 'Soňky täzeden başlatma:',
      quotaSettings: 'Kwota sazlamalary',
      enableWordQuotas: 'Söz kwotalaryny işjeňleşdirmek',
      setLimitsOnWordUsage: 'Söz ulanyşyna çäklendirmeleri goýmak',
      wordLimit: 'Söz çägi',
      maximumWordsPerResetPeriod: 'Täzeden başlatma döwri üçin iň köp sözler',
      resetPeriod: 'Täzeden başlatma döwri',
      daily: 'Gündelik',
      weekly: 'Hepdelik',
      monthly: 'Aýlyk',
      neverManualResetOnly: 'Hiç haçan (diňe el bilen täzeden başlatma)',
      howOftenUsageStatsReset: 'Ulanyş statistikasy näçe wagtdan täzeden başlaýar',

      // Image Usage Modal
      trackImageProcessingUsage: 'Surat işlemek ulanyşyňyzy yzarlaň',
      imagesUsage: 'Surat ulanyşy',
      nearLimit: 'Çäklä ýakyn',
      warning: 'Duýduryş',
      good: 'Gowy',
      imagesUsed: 'Ulanylýan suratlar',
      remaining: 'Galdy',
      imagesRemaining: 'surat galdy',
      imagesUsedText: 'surat ulanyldy',
      imagesAvailable: 'surat elýeterli',
      statistics: 'Statistika',
      totalImagesProcessed: 'Jemi işlenen suratlar:',
      lastActive: 'Soňky işjeňlik:',
      never: 'Hiç haçan',
      accountCreated: 'Hasap döredildi:',
      imageLimitReached: 'Surat çägi ýetildi',
      imageLimitReachedMessage: 'Siz surat işlemek çägiňize ýetdiňiz. Dowam etdirmek üçin has köp surat satyn alyň.',
      loadingImageUsageData: 'Surat ulanyş maglumatlary ýüklenýär...',

      // Status Messages
      hello: 'Salam',
      processing: 'Işleýär...',
      recording: 'Ýazýar...',
      translating: 'Terjime edýär...',
      error: 'Ýalňyşlyk',
      success: 'Üstünlik',

      // Success Messages (using English as placeholder - add translations later)
      wordsAddedSuccessfully: 'Successfully added {0} words to your account!',
      imagesAddedSuccessfully: 'Successfully added {0} images to your account!',
      textExtractedSuccessfully: 'Text extracted from image successfully!',
      audioGeneratedSuccessfully: 'Audio generated successfully!',
      originalAudioGeneratedSuccessfully: 'Original audio generated successfully!',
      usageStatsResetSuccessfully: 'Usage statistics have been reset successfully.',
      purchaseSuccessful: 'Purchase successful!',
      paymentSuccessful: 'Payment successful!',

      // Error Messages (using English as placeholder - add translations later)
      wordLimitExceeded: 'Word limit exceeded',
      wordLimitExceededTitle: 'Word Limit Exceeded',
      wordLimitExceededMessage: 'Word limit exceeded. Please buy more words to continue.',
      wordLimitExceededDetail: "You've used {0} out of {1} words. Please buy more words to continue.",
      textExceedsWordLimit: 'Text exceeds 200 words limit. Please shorten your message.',
      pleaseBuyMoreWords: 'Please buy more words',
      pleaseBuyMoreWordsToContinue: 'Please buy more words to continue.',
      purchaseFailed: 'Failed to purchase',
      purchaseFailedTryAgain: 'Failed to purchase. Please try again.',
      readImageFailed: 'Failed to read text from image',
      noTextFoundInImage: 'No text found in the image',
      translationFailed: 'Translation failed',
      translationFailedTryAgain: 'Translation failed. Please try again.',
      wordsRemainingWarning: 'Warning: You have only {0} words remaining ({1}% used). Consider buying more words soon.',
      approachingWordLimit: 'Approaching word limit',

      // Continuous Conversation Modal
      continuousConversation: 'Dowamly söhbet',
      bidirectionalConversation: 'Iki ugurly söhbet',
      readyToTranscribe: 'Transkripsiya üçin taýýar',
      transcribing: 'Transkripsiya...',
      startTranscribing: 'Transkripsiýany başla',
      stopTranscribing: 'Transkripsiýany duruz',
      transcripts: 'Transkripsiyalar',
      conversation: 'Söhbet',
      noTranscriptsYet: 'Transkripsiyalar ýok. Başlamak üçin transkripsiýany başlaň!',
      buyMoreWords: 'Buy More Words',
      dismiss: 'Dismiss',
      lectureMode: 'Leksiýa režimi',
      lectureModeDescription: 'Gowy terjime konteksti üçin her 20 sekuntda ýazmak',
      advancedModel: 'Ösen terjime modeli',
      advancedModelDescription: 'gpt-5.4-mini ulanýar. Has haýal bolup biler.',
      liveTranslation: "Göni terjime",
      liveTranslationDescription: "Gürläp durkaňyz terjime edýär (gpt-realtime-translate): Üznüksiz söhbetdeşlik we Ýaýlym režimlerinde. Tanalýan diller: iňlis, koreý, hytaý (mandarin), rus, gazak, özbek, pars, ispan. Ses bilen terjime: iňlis, koreý, hytaý (mandarin), rus, ispan.",
      liveTranslationFallback: "Göni terjime {0} → {1} goldamaýar. Adaty terjime ulanylýar.",
      liveTranslationNotSupported: "göni terjimede ýok",

      // Broadcast Translation Modal
      broadcastTranslation: 'Terjime ýaýlymy',
      speaker: 'Gepleýän',
      listener: 'Diňleýän',
      sourceLanguage: 'Başlangyç dil',
      sourceLanguageSet: 'Başlangyç dil bölümiňizden düzülýär',
      targetLanguage: 'Niýetlenen dil',
      addLanguage: 'Dil goşmak',
      broadcastCode: 'Ýaýlym kody',
      shareCode: 'Bu kody diňleýjiler bilen paýlaşyň',
      startBroadcast: 'Ýaýlymy başlamak',
      stopBroadcast: 'Ýaýlymy duruzmak',
      enterCode: 'Ýaýlym koduny giriziň',
      selectLanguage: 'Dili saýlaň',
      joinBroadcast: 'Ýaýlyma goşulmak',
      listeningTo: 'Diňlemekde',
      leaveBroadcast: 'Ýaýlymdan çykmak',
      messages: 'Habar',
      waitingForMessages: 'Habarlary garaşmakda...',
      availableLanguages: 'Elýeterli diller',
      conversationTopic: 'Gepleşik möwzusy (mecburi däl)',
      topicPlaceholder: 'Meselem: Tibbi maslahat, Iş duşuşygy, Syýahat meýilnamasy...',
      topicHint: 'Möwzuny goşmak terjimäniň takyklygyny gowulaşdyrýar',
      audioOn: 'Audio: AÇYK',
      audioOff: 'Audio: ÝAPYK',
      speaking: 'Gürleýär...',
      wordCountWarning: 'Bellik: Her goşmaça dil sözleriň sanyny köpeldýär (meselem, 2 dil = 2 esse, 3 dil = 3 esse)',

      // Auth0 Error Messages
      auth0LoginFailed: 'Auth0 üsti bilen girmek şowsuz boldy',
      auth0LoginTimedOut: 'Auth0 üsti bilen girmek wagty gutardy',
      auth0RegistrationFailed: 'Auth0-da hasaba alyşmak şowsuz boldy',
      auth0RegistrationTimedOut: 'Auth0-da hasaba alyşmak wagty gutardy',
      invalidAuthResponse: 'Nädogry autentifikasiýa jogaby',
      facebookLoginDenied: 'Facebook üsti bilen girmek ýatyryldy',
      facebookPermissionsRequired: 'Facebook rugsatnamalary gerek',
      loginRequired: 'Girmek gerek',
      notMobilePlatform: 'Bu funksiýa diňe mobil platformalarda elýeterli',
      transcriptionTimeout: 'Transkripsiya wagty gutardy',
      transcriptionTimeoutTitle: 'Transkripsiya wagty gutardy',
      transcriptionTimeoutMessage: 'Transkripsiya soragy köp wagt aldy. Gaýtadan synanyşyp ýörýäris...',
      transcriptionFailed: 'Transkripsiya şowsuz boldy. Gaýtadan synanyşyň.',
      retryingTranscription: 'Transkripsiyany gaýtadan synanyşyp ýörýäris...',

      // Support Page
      supportAndFeedback: 'Goldaw we pikir',
      supportHeaderSubtitle: 'Biz bu ýerde size kömek etmäge taýyn we pikirleriňizi eşitmek isleýäris',
      supportThankYou: 'Ulanyşyňyz üçin sag boluň',
      supportFeedbackInvaluable: 'Pikirleriňiz biziň üçin gymmatly',
      supportImproveServices: 'biz terjime hyzmatlaryny gowulaşdyrmagy dowam etdirip',
      supportMultilingualCommunication: 'we köpdilli aragatnaşygy',
      supportQuestionsSuggestions: 'Soraglaryňyz, teklipleriňiz bolsa ýa-da',
      supportTechnicalSupport: 'tehniki goldaw gerek bolsa,',
      supportShareExperience: 'ýa-da TilHub bilen bolan tejribäňizi paýlaşmak isleseňiz,',
      supportLoveToHear: 'biz pikirleriňizi eşitmek isleýäris.',
      supportGetInTouch: 'Habarlaşmak',
      supportContactUs: 'Pikir, sorag ýa-da goldaw üçin aşakdaky salgymyz arkaly biz bilen habarlaşyň:',
      supportSendEmail: 'E-poçta ibermek',
      supportCopyEmailAddress: 'E-poçta salgysyny göçürmek',
      supportEmailCopied: 'E-poçta salgysy çalşyryş buferine göçürildi!',
      supportBackToApp: 'Ulgama gaýtmak',
      supportDeleteAccount: 'Hasabyňyzy pozmak',
      supportDeleteAccountDescription: 'Hasabyňyzy we degişli ähli maglumatlary islendik wagtda pozmak üçin haýyş edip bilersiňiz.',
      supportRequestAccountDeletion: 'Hasaby pozmagy haýyş et',
      supportDeleteAccountConfirmation: 'Are you sure you want to delete your account? This action cannot be undone. All your data will be permanently deleted.',
      supportDeleteAccountFinalConfirm: 'This is your final warning. Your account and all data will be permanently deleted. Are you absolutely sure?',
      supportAccountDeleted: 'Your account has been deleted successfully. You will be logged out now.',
      supportDeleteAccountError: 'Failed to delete account. Please try again or contact support.',
      supportDeletingAccount: 'Deleting...',
      support: 'Goldaw',
    },

    // Uzbek (O'zbekcha)
    uz: {
      // Navigation
      languages: 'Tillar',
      languageSettings: 'Til sozlamalari',
      voiceType: 'Ovoz turi',
      account: 'Hisob',
      profile: 'Profil',
      settings: 'Sozlamalar',
      history: 'Tarix',
      wordUsage: "So'z ishlatish",
      imageUsage: 'Rasm ishlatish',
      analytics: 'Analitika',
      buyWords: "Tarjima ma'lumotlarini sotib olish",
      buyImages: "Rasm ma'lumotlarini sotib olish",
      buyDataFromWebapp: "Veb-ilovadan ma'lumot sotib olish",
      login: 'Kirish',
      register: "Ro'yxatdan o'tish",
      logout: 'Chiqish',

      // Buy Modals
      buyWordsTitle: "So'zlarni sotib olish",
      buyWordsSubtitle: "Tarjima uchun qo'shimcha ma'lumotlar sotib olish",
      buyImagesTitle: 'Rasmlarni sotib olish',
      buyImagesSubtitle:
        "Rasm ishlov berish uchun qo'shimcha kreditlar sotib olish",
      conversationTopicPlaceholder: "Suhbat mavzusi (masalan, stomatologiya ma'ruzasi, tibbiy konferentsiya)",
      conversationTopicHint: "Ixtiyoriy: ma'lum mavzular uchun tarjima aniqligini yaxshilashga yordam beradi",
      selectWordPackage: "So'z paketini tanlash",
      selectImagePackage: 'Rasm paketini tanlash',
      oneTimePurchase: 'Bir martalik sotib olish',
      buyMoreSaveMore: "Ko'proq sotib olsangiz, ko'proq tejaysiz!",
      popular: 'Mashhur',
      data: "ma'lumot",
      wordCredits: "so'z kreditlari",
      imageProcessingCredits: 'rasm ishlov berish kreditlari',
      perfectForTesting: 'Test uchun mukammal',
      save: 'Tejash',
      selectAnOption: 'Variantni tanlash',
      howItWorks: 'Qanday ishlaydi:',
      oneTimePurchaseAddsWords:
        "Bir martalik sotib olish so'zlarni hisobingizga doimiy qo'shadi",
      wordsConsumedAsYouUse:
        "Tarjima funksiyalarini ishlatganda so'zlar sarflanadi",
      noExpirationUseWords:
        "Muddati yo'q - kerak bo'lganda so'zlarni ishlating",
      oneTimePurchaseAddsImages:
        "Bir martalik sotib olish rasmlarni hisobingizga doimiy qo'shadi",
      imagesConsumedWhenYouUseOCR:
        'Matn olishni ishlatganda rasmlar sarflanadi',
      noExpirationUseImages:
        "Muddati yo'q - kerak bo'lganda rasmlarni ishlating",

      // Language Selection
      yourLanguage: 'Sizning tilingiz:',
      partnerLanguage: 'Sherikning tili:',
      speakingSpeed: 'Gapirish tezligi:',
      slow: 'Sekin',
      fast: 'Tez',

      // Main App
      translation: 'Tarjima',
      start: 'Boshlash',
      end: 'Tugatish',
      clear: 'Tozalash',
      clearHistory: 'Tarixni tozalash',
      showAll: "Hammasini ko'rsatish",
      readImage: "Rasmni o'qish",
      translate: 'Tarjima qilish',
      typeMessage: 'Xabaringizni bu yerga yozing...',
      typePartnerMessage: 'Sherigingizning xabarini bu yerga yozing...',
      maxWords: "maks 200 so'z",
      words: "so'z",
      characters: 'belgi',

      // Conversation
      youSpeakLabel: 'Siz gapirasiz',
      partnerSpeaksLabel: 'Sherik gapiradi',

      // History Modal
      conversationHistory: 'Suhbat tarixi',
      searchConversations: 'Suhbatlarni qidirish...',
      allAudioFiles: 'Barcha audio fayllar',
      clearAll: 'Hammasini tozalash',
      downloadAll: 'Hammasini yuklash',
      play: 'Ijro etish',
      delete: "O'chirish",
      confidence: 'Ishonch',
      recorded: 'Yozilgan',
      conversations: 'suhbatlar',
      totalDuration: 'umumiy davomiyligi',
      noConversationsFound: 'Suhbatlar topilmadi',
      noConversationsYet: "Hozircha suhbatlar yo'q",
      tryAdjustingSearch: 'Qidiruv shartlarini sozlang',
      startConversationToSee: "Ko'rish uchun suhbat boshlang",
      youWrote: 'Siz yozdingiz',
      youSpoke: 'Siz aytdingiz',
      partnerWrote: 'Hamkor yozdi',
      partnerHears: 'Hamkor eshitadi',
      noOriginalText: "Asl matn yo'q",
      noTranslation: "Tarjima yo'q",
      download: 'Yuklash',
      generateAudioFor: 'Audio yaratish ',
      generating: 'Yaratilmoqda...',

      // Word Usage Modal
      wordUsageAndQuotas: "So'z ishlatish va kvotalar",
      trackTranslationUsage: "Tarjima foydalanishingizni kuzating va kvotalarni boshqaring",
      currentUsage: 'Joriy foydalanish',
      wordsUsage: "So'z ishlatish",
      switchToTotalView: "Umumiy ko'rinishga o'tish",
      switchToRemainingView: "Qolgan ko'rinishga o'tish",
      of: '/',
      lastReset: "Oxirgi qayta o'rnatish:",
      quotaSettings: 'Kvota sozlamalari',
      enableWordQuotas: "So'z kvotalarini yoqish",
      setLimitsOnWordUsage: "So'z ishlatishga cheklovlar o'rnatish",
      wordLimit: "So'z chegarasi",
      maximumWordsPerResetPeriod: "Qayta o'rnatish davri uchun maksimal so'zlar",
      resetPeriod: "Qayta o'rnatish davri",
      daily: 'Kunlik',
      weekly: 'Haftalik',
      monthly: 'Oylik',
      neverManualResetOnly: "Hech qachon (faqat qo'lda qayta o'rnatish)",
      howOftenUsageStatsReset: "Foydalanish statistikasi qancha vaqtda qayta o'rnatiladi",

      // Image Usage Modal
      trackImageProcessingUsage: "Rasm qayta ishlash foydalanishingizni kuzating",
      imagesUsage: 'Rasmlar foydalanishi',
      nearLimit: 'Chegara yaqin',
      warning: 'Ogohlantirish',
      good: 'Yaxshi',
      imagesUsed: "Ishlatilgan rasmlar",
      remaining: 'Qolgan',
      imagesRemaining: 'rasm qoldi',
      imagesUsedText: 'rasm ishlatildi',
      imagesAvailable: 'rasm mavjud',
      statistics: 'Statistika',
      totalImagesProcessed: "Jami qayta ishlangan rasmlar:",
      lastActive: "Oxirgi faollik:",
      never: "Hech qachon",
      accountCreated: 'Hisob yaratilgan:',
      imageLimitReached: 'Rasm chegarasiga yetildi',
      imageLimitReachedMessage: "Siz rasm qayta ishlash chegarasiga yetdingiz. Davom etish uchun ko'proq rasm sotib oling.",
      loadingImageUsageData: 'Rasm foydalanish ma\'lumotlari yuklanmoqda...',

      // Status Messages
      hello: 'Salom',
      processing: 'Ishlanmoqda...',
      recording: 'Yozilmoqda...',
      translating: 'Tarjima qilinmoqda...',
      error: 'Xatolik',
      success: 'Muvaffaqiyat',

      // Success Messages (using English as placeholder - add translations later)
      wordsAddedSuccessfully: 'Successfully added {0} words to your account!',
      imagesAddedSuccessfully: 'Successfully added {0} images to your account!',
      textExtractedSuccessfully: 'Text extracted from image successfully!',
      audioGeneratedSuccessfully: 'Audio generated successfully!',
      originalAudioGeneratedSuccessfully: 'Original audio generated successfully!',
      usageStatsResetSuccessfully: 'Usage statistics have been reset successfully.',
      purchaseSuccessful: 'Purchase successful!',
      paymentSuccessful: 'Payment successful!',

      // Error Messages (using English as placeholder - add translations later)
      wordLimitExceeded: 'Word limit exceeded',
      wordLimitExceededTitle: 'Word Limit Exceeded',
      wordLimitExceededMessage: 'Word limit exceeded. Please buy more words to continue.',
      wordLimitExceededDetail: "You've used {0} out of {1} words. Please buy more words to continue.",
      textExceedsWordLimit: 'Text exceeds 200 words limit. Please shorten your message.',
      pleaseBuyMoreWords: 'Please buy more words',
      pleaseBuyMoreWordsToContinue: 'Please buy more words to continue.',
      purchaseFailed: 'Failed to purchase',
      purchaseFailedTryAgain: 'Failed to purchase. Please try again.',
      readImageFailed: 'Failed to read text from image',
      noTextFoundInImage: 'No text found in the image',
      translationFailed: 'Translation failed',
      translationFailedTryAgain: 'Translation failed. Please try again.',
      wordsRemainingWarning: 'Warning: You have only {0} words remaining ({1}% used). Consider buying more words soon.',
      approachingWordLimit: 'Approaching word limit',

      // Continuous Conversation Modal
      continuousConversation: "Uzluksiz suhbat",
      bidirectionalConversation: 'Ikki yo\'nalishli suhbat',
      readyToTranscribe: 'Transkripsiya uchun tayyor',
      transcribing: 'Transkripsiya...',
      startTranscribing: 'Transkripsiyani boshlash',
      stopTranscribing: 'Transkripsiyani to\'xtatish',
      transcripts: 'Transkripsiyalar',
      conversation: 'Suhbat',
      noTranscriptsYet: 'Transkripsiyalar hali yo\'q. Boshlash uchun transkripsiyani boshlang!',
      buyMoreWords: 'Buy More Words',
      dismiss: 'Dismiss',
      lectureMode: 'Ma\'ruza rejimi',
      lectureModeDescription: 'Yaxshi tarjima konteksti uchun har 20 soniyada yozish',
      advancedModel: 'Kengaytirilgan tarjima modeli',
      advancedModelDescription: 'gpt-5.4-mini ishlatadi. Sekinroq bo‘lishi mumkin.',
      liveTranslation: "Sinxron tarjima",
      liveTranslationDescription: "Gapirayotganingizda tarjima qiladi (gpt-realtime-translate): Uzluksiz suhbat va Translyatsiya rejimlarida. Taniladigan tillar: ingliz, koreys, xitoy (mandarin), rus, qozoq, oʻzbek, fors, ispan. Ovozli tarjima: ingliz, koreys, xitoy (mandarin), rus, ispan.",
      liveTranslationFallback: "Sinxron tarjima {0} → {1} ni qoʻllab-quvvatlamaydi. Oddiy tarjima ishlatiladi.",
      liveTranslationNotSupported: "sinxron tarjimada mavjud emas",

      // Broadcast Translation Modal
      broadcastTranslation: 'Tarjima translyatsiyasi',
      speaker: 'So\'zlovchi',
      listener: 'Tinglovchi',
      sourceLanguage: 'Manba til',
      sourceLanguageSet: 'Manba til bo\'limingizdan o\'rnatilgan',
      targetLanguage: 'Maqsadli til',
      addLanguage: 'Til qo\'shish',
      broadcastCode: 'Translyatsiya kodi',
      shareCode: 'Bu kodni tinglovchilar bilan baham ko\'ring',
      startBroadcast: 'Translyatsiyani boshlash',
      stopBroadcast: 'Translyatsiyani to\'xtatish',
      enterCode: 'Translyatsiya kodini kiriting',
      selectLanguage: 'Tilni tanlang',
      joinBroadcast: 'Translyatsiyaga qo\'shilish',
      listeningTo: 'Tinglamoqda',
      leaveBroadcast: 'Translyatsiyadan chiqish',
      messages: 'Xabarlar',
      waitingForMessages: 'Xabarlarni kutmoqda...',
      availableLanguages: 'Mavjud tillar',
      conversationTopic: 'Suhbat mavzusi (ixtiyoriy)',
      topicPlaceholder: 'Masalan: Tibbiy maslahat, Biznes uchrashuvi, Sayohat rejasi...',
      topicHint: 'Mavzuni qo\'shish tarjima aniqligini yaxshilaydi',
      audioOn: 'Audio: YOQILGAN',
      audioOff: 'Audio: O\'CHIRILGAN',
      speaking: 'Gapirayapti...',
      wordCountWarning: 'Eslatma: Har qo\'shimcha til so\'zlar sonini ko\'paytiradi (masalan, 2 til = 2 marta, 3 til = 3 marta)',

      // Auth0 Error Messages
      auth0LoginFailed: 'Auth0 orqali kirish muvaffaqiyatsiz',
      auth0LoginTimedOut: 'Auth0 orqali kirish vaqti tugadi',
      auth0RegistrationFailed: 'Auth0-da ro\'yxatdan o\'tish muvaffaqiyatsiz',
      auth0RegistrationTimedOut: 'Auth0-da ro\'yxatdan o\'tish vaqti tugadi',
      invalidAuthResponse: 'Noto\'g\'ri autentifikatsiya javobi',
      facebookLoginDenied: 'Facebook orqali kirish rad etildi',
      facebookPermissionsRequired: 'Facebook ruxsatlari talab qilinadi',
      loginRequired: 'Kirish talab qilinadi',
      notMobilePlatform: 'Bu funksiya faqat mobil platformalarda mavjud',
      transcriptionTimeout: 'Transkripsiya vaqti tugadi',
      transcriptionTimeoutTitle: 'Transkripsiya vaqti tugadi',
      transcriptionTimeoutMessage: 'Transkripsiya so\'rovi ko\'p vaqt oldi. Qayta urinmoqdamiz...',
      transcriptionFailed: 'Transkripsiya muvaffaqiyatsiz bo\'ldi. Qayta urinib ko\'ring.',
      retryingTranscription: 'Transkripsiyani qayta urinmoqdamiz...',

      // Support Page
      supportAndFeedback: 'Yordam va fikr',
      supportHeaderSubtitle: 'Biz sizga yordam berishga tayyormiz va fikrlaringizni eshitishni xohlaymiz',
      supportThankYou: 'Foydalanganingiz uchun rahmat',
      supportFeedbackInvaluable: 'Fikrlaringiz biz uchun qadrli',
      supportImproveServices: 'biz tarjima xizmatlarini yaxshilashni davom ettiramiz',
      supportMultilingualCommunication: 'va ko\'p tilli muloqotni',
      supportQuestionsSuggestions: 'Savollaringiz, takliflaringiz bo\'lsa yoki',
      supportTechnicalSupport: 'texnik yordam kerak bo\'lsa,',
      supportShareExperience: 'yoki TilHub bilan bo\'lgan tajribangizni baham ko\'rmoqchi bo\'lsangiz,',
      supportLoveToHear: 'biz fikrlaringizni eshitishni xohlaymiz.',
      supportGetInTouch: 'Bog\'lanish',
      supportContactUs: 'Fikr, savol yoki yordam uchun quyidagi manzilimiz orqali biz bilan bog\'laning:',
      supportSendEmail: 'Elektron pochta yuborish',
      supportCopyEmailAddress: 'Elektron pochta manzilini nusxalash',
      supportEmailCopied: 'Elektron pochta manzili buferga nusxalandi!',
      supportBackToApp: 'Ilovaga qaytish',
      supportDeleteAccount: 'Hisobingizni o\'chirish',
      supportDeleteAccountDescription: 'Hisobingizni va barcha bog\'liq ma\'lumotlarni istalgan vaqtda o\'chirishni so\'rash mumkin.',
      supportRequestAccountDeletion: 'Hisobni o\'chirishni so\'rash',
      supportDeleteAccountConfirmation: 'Are you sure you want to delete your account? This action cannot be undone. All your data will be permanently deleted.',
      supportDeleteAccountFinalConfirm: 'This is your final warning. Your account and all data will be permanently deleted. Are you absolutely sure?',
      supportAccountDeleted: 'Your account has been deleted successfully. You will be logged out now.',
      supportDeleteAccountError: 'Failed to delete account. Please try again or contact support.',
      supportDeletingAccount: 'Deleting...',
      support: 'Qo\'llab-quvvatlash',
    },

    // Farsi (فارسی)
    fa: {
      // Navigation
      languages: 'زبان‌ها',
      languageSettings: 'تنظیمات زبان',
      voiceType: 'نوع صدا',
      account: 'حساب',
      profile: 'پروفایل',
      settings: 'تنظیمات',
      history: 'تاریخچه',
      wordUsage: 'استفاده از کلمات',
      imageUsage: 'استفاده از تصاویر',
      analytics: 'تحلیل',
      buyWords: 'خرید داده‌های ترجمه',
      buyImages: 'خرید داده‌های تصویر',
      buyDataFromWebapp: 'خرید داده از برنامه وب',
      login: 'ورود',
      register: 'ثبت‌نام',
      logout: 'خروج',

      // Buy Modals
      buyWordsTitle: 'خرید کلمات',
      buyWordsSubtitle: 'خرید داده‌های ترجمه اضافی',
      buyImagesTitle: 'خرید تصاویر',
      buyImagesSubtitle: 'خرید اعتبارات اضافی پردازش تصویر',
      conversationTopicPlaceholder: 'موضوع گفتگو (مثلاً: سخنرانی دندانپزشکی، کنفرانس پزشکی)',
      conversationTopicHint: 'اختیاری: به بهبود دقت ترجمه برای موضوعات خاص کمک می‌کند',
      selectWordPackage: 'انتخاب بسته کلمات',
      selectImagePackage: 'انتخاب بسته تصویر',
      oneTimePurchase: 'خرید یکباره',
      buyMoreSaveMore: 'هرچه بیشتر بخرید، بیشتر صرفه‌جویی می‌کنید!',
      popular: 'محبوب',
      data: 'داده',
      wordCredits: 'اعتبارات کلمه',
      imageProcessingCredits: 'اعتبارات پردازش تصویر',
      perfectForTesting: 'عالی برای تست',
      save: 'صرفه‌جویی',
      selectAnOption: 'انتخاب گزینه',
      howItWorks: 'نحوه کار:',
      oneTimePurchaseAddsWords: 'خرید یکباره کلمات را به طور دائمی به حساب شما اضافه می‌کند',
      wordsConsumedAsYouUse: 'کلمات هنگام استفاده از ویژگی‌های ترجمه مصرف می‌شوند',
      noExpirationUseWords: 'بدون انقضا - هر زمان که نیاز دارید از کلمات استفاده کنید',
      oneTimePurchaseAddsImages: 'خرید یکباره تصاویر را به طور دائمی به حساب شما اضافه می‌کند',
      imagesConsumedWhenYouUseOCR: 'تصاویر هنگام استفاده از استخراج متن مصرف می‌شوند',
      noExpirationUseImages: 'بدون انقضا - هر زمان که نیاز دارید از تصاویر استفاده کنید',

      // Language Selection
      yourLanguage: 'زبان شما:',
      partnerLanguage: 'زبان طرف مقابل:',
      speakingSpeed: 'سرعت گفتار:',
      slow: 'کند',
      fast: 'سریع',

      // Main App
      translation: 'ترجمه',
      start: 'شروع',
      end: 'پایان',
      clear: 'پاک کردن',
      clearHistory: 'پاک کردن تاریخچه',
      showAll: 'نمایش همه',
      readImage: 'خواندن تصویر',
      translate: 'ترجمه',
      typeMessage: 'پیام خود را اینجا تایپ کنید...',
      typePartnerMessage: 'پیام طرف مقابل را اینجا تایپ کنید...',
      maxWords: 'حداکثر ۲۰۰ کلمه',
      words: 'کلمه',
      characters: 'کاراکتر',

      // Conversation
      youSpeakLabel: 'شما می‌گویید',
      partnerSpeaksLabel: 'طرف مقابل می‌گوید',

      // History Modal
      conversationHistory: 'تاریخچه گفتگو',
      searchConversations: 'جستجوی گفتگوها...',
      allAudioFiles: 'همه فایل‌های صوتی',
      clearAll: 'پاک کردن همه',
      downloadAll: 'دانلود همه',
      play: 'پخش',
      delete: 'حذف',
      confidence: 'اعتماد',
      recorded: 'ضبط شده',
      conversations: 'گفتگوها',
      totalDuration: 'مدت زمان کل',
      noConversationsFound: 'گفتگویی یافت نشد',
      noConversationsYet: 'هنوز گفتگویی وجود ندارد',
      tryAdjustingSearch: 'سعی کنید شرایط جستجو را تنظیم کنید',
      startConversationToSee: 'برای دیدن گفتگو را شروع کنید',
      youWrote: 'شما نوشتید',
      youSpoke: 'شما گفتید',
      partnerWrote: 'طرف مقابل نوشت',
      partnerHears: 'طرف مقابل می‌شنود',
      noOriginalText: 'متن اصلی وجود ندارد',
      noTranslation: 'ترجمه وجود ندارد',
      download: 'دانلود',
      generateAudioFor: 'تولید صدا برای ',
      generating: 'در حال تولید...',

      // Word Usage Modal
      wordUsageAndQuotas: 'استفاده از کلمات و سهمیه‌ها',
      trackTranslationUsage: 'استفاده از ترجمه خود را ردیابی کنید و سهمیه‌ها را مدیریت کنید',
      currentUsage: 'استفاده فعلی',
      wordsUsage: 'استفاده از کلمات',
      switchToTotalView: 'تغییر به نمای کل',
      switchToRemainingView: 'تغییر به نمای باقیمانده',
      of: '/',
      lastReset: 'بازنشانی آخر:',
      quotaSettings: 'تنظیمات سهمیه',
      enableWordQuotas: 'فعال‌سازی سهمیه کلمات',
      setLimitsOnWordUsage: 'تعیین محدودیت‌های استفاده از کلمات',
      wordLimit: 'محدودیت کلمات',
      maximumWordsPerResetPeriod: 'حداکثر کلمات در هر دوره بازنشانی',
      resetPeriod: 'دوره بازنشانی',
      daily: 'روزانه',
      weekly: 'هفتگی',
      monthly: 'ماهانه',
      neverManualResetOnly: 'هرگز (فقط بازنشانی دستی)',
      howOftenUsageStatsReset: 'چند وقت یکبار آمار استفاده بازنشانی می‌شود',

      // Image Usage Modal
      trackImageProcessingUsage: 'استفاده از پردازش تصویر خود را ردیابی کنید',
      imagesUsage: 'استفاده از تصاویر',
      nearLimit: 'نزدیک به حد',
      warning: 'هشدار',
      good: 'خوب',
      imagesUsed: 'تصاویر استفاده شده',
      remaining: 'باقیمانده',
      imagesRemaining: 'تصویر باقی مانده',
      imagesUsedText: 'تصویر استفاده شده',
      imagesAvailable: 'تصویر در دسترس',
      statistics: 'آمار',
      totalImagesProcessed: 'کل تصاویر پردازش شده:',
      lastActive: 'آخرین فعالیت:',
      never: 'هرگز',
      accountCreated: 'حساب ایجاد شده:',
      imageLimitReached: 'حد تصویر رسیده است',
      imageLimitReachedMessage: 'شما به حد پردازش تصویر رسیده‌اید. برای ادامه تصاویر بیشتری خریداری کنید.',
      loadingImageUsageData: 'در حال بارگذاری داده‌های استفاده از تصویر...',

      // Status Messages
      hello: 'سلام',
      processing: 'در حال پردازش...',
      recording: 'در حال ضبط...',
      translating: 'در حال ترجمه...',
      error: 'خطا',
      success: 'موفقیت',

      // Success Messages (using English as placeholder - add translations later)
      wordsAddedSuccessfully: 'Successfully added {0} words to your account!',
      imagesAddedSuccessfully: 'Successfully added {0} images to your account!',
      textExtractedSuccessfully: 'Text extracted from image successfully!',
      audioGeneratedSuccessfully: 'Audio generated successfully!',
      originalAudioGeneratedSuccessfully: 'Original audio generated successfully!',
      usageStatsResetSuccessfully: 'Usage statistics have been reset successfully.',
      purchaseSuccessful: 'Purchase successful!',
      paymentSuccessful: 'Payment successful!',

      // Error Messages (using English as placeholder - add translations later)
      wordLimitExceeded: 'Word limit exceeded',
      wordLimitExceededTitle: 'Word Limit Exceeded',
      wordLimitExceededMessage: 'Word limit exceeded. Please buy more words to continue.',
      wordLimitExceededDetail: "You've used {0} out of {1} words. Please buy more words to continue.",
      textExceedsWordLimit: 'Text exceeds 200 words limit. Please shorten your message.',
      pleaseBuyMoreWords: 'Please buy more words',
      pleaseBuyMoreWordsToContinue: 'Please buy more words to continue.',
      purchaseFailed: 'Failed to purchase',
      purchaseFailedTryAgain: 'Failed to purchase. Please try again.',
      readImageFailed: 'Failed to read text from image',
      noTextFoundInImage: 'No text found in the image',
      translationFailed: 'Translation failed',
      translationFailedTryAgain: 'Translation failed. Please try again.',
      wordsRemainingWarning: 'Warning: You have only {0} words remaining ({1}% used). Consider buying more words soon.',
      approachingWordLimit: 'Approaching word limit',

      // Continuous Conversation Modal
      continuousConversation: 'گفتگوی مداوم',
      bidirectionalConversation: 'گفتگوی دو طرفه',
      readyToTranscribe: 'آماده برای رونویسی',
      transcribing: 'در حال رونویسی...',
      startTranscribing: 'شروع رونویسی',
      stopTranscribing: 'توقف رونویسی',
      transcripts: 'رونویسی‌ها',
      conversation: 'گفتگو',
      noTranscriptsYet: 'هنوز رونویسی وجود ندارد. برای شروع رونویسی را شروع کنید!',
      buyMoreWords: 'Buy More Words',
      dismiss: 'Dismiss',
      lectureMode: 'حالت سخنرانی',
      lectureModeDescription: 'برای زمینه بهتر ترجمه هر 20 ثانیه ضبط کنید',
      advancedModel: 'مدل ترجمه پیشرفته',
      advancedModelDescription: 'از gpt-5.4-mini استفاده می‌کند. ممکن است کندتر باشد.',
      liveTranslation: "ترجمه همزمان",
      liveTranslationDescription: "هنگام صحبت ترجمه می‌کند (gpt-realtime-translate) در گفتگوی پیوسته و پخش. زبان‌های گفتار: انگلیسی، کره‌ای، چینی ماندارین، روسی، قزاقی، ازبکی، فارسی، اسپانیایی. خروجی صوتی: انگلیسی، کره‌ای، چینی ماندارین، روسی، اسپانیایی.",
      liveTranslationFallback: "ترجمه همزمان از {0} → {1} پشتیبانی نمی‌کند. از ترجمه معمولی استفاده می‌شود.",
      liveTranslationNotSupported: "در ترجمه همزمان پشتیبانی نمی‌شود",

      // Broadcast Translation Modal
      broadcastTranslation: 'پخش ترجمه',
      speaker: 'گوینده',
      listener: 'شنونده',
      sourceLanguage: 'زبان مبدأ',
      sourceLanguageSet: 'زبان مبدأ از بخش شما تنظیم شده است',
      targetLanguage: 'زبان مقصد',
      addLanguage: 'افزودن زبان',
      broadcastCode: 'کد پخش',
      shareCode: 'این کد را با شنوندگان به اشتراک بگذارید',
      startBroadcast: 'شروع پخش',
      stopBroadcast: 'توقف پخش',
      enterCode: 'کد پخش را وارد کنید',
      selectLanguage: 'زبان را انتخاب کنید',
      joinBroadcast: 'پیوستن به پخش',
      listeningTo: 'در حال گوش دادن',
      leaveBroadcast: 'ترک پخش',
      messages: 'پیام‌ها',
      waitingForMessages: 'در انتظار پیام‌ها...',
      availableLanguages: 'زبان‌های موجود',
      conversationTopic: 'موضوع گفتگو (اختیاری)',
      topicPlaceholder: 'مثال: مشاوره پزشکی، جلسه تجاری، برنامه‌ریزی سفر...',
      topicHint: 'افزودن موضوع به بهبود دقت ترجمه کمک می‌کند',
      audioOn: 'صدا: روشن',
      audioOff: 'صدا: خاموش',
      speaking: 'در حال صحبت...',
      wordCountWarning: 'توجه: هر زبان اضافی تعداد کلمات را چند برابر می‌کند (مثلاً: 2 زبان = 2 برابر، 3 زبان = 3 برابر)',

      // Auth0 Error Messages
      auth0LoginFailed: 'ورود به Auth0 ناموفق بود',
      auth0LoginTimedOut: 'زمان ورود به Auth0 به پایان رسید',
      auth0RegistrationFailed: 'ثبت نام در Auth0 ناموفق بود',
      auth0RegistrationTimedOut: 'زمان ثبت نام در Auth0 به پایان رسید',
      invalidAuthResponse: 'پاسخ احراز هویت نامعتبر',
      facebookLoginDenied: 'ورود به Facebook رد شد',
      facebookPermissionsRequired: 'مجوزهای Facebook مورد نیاز است',
      loginRequired: 'ورود مورد نیاز است',
      notMobilePlatform: 'این ویژگی فقط در پلتفرم‌های موبایل در دسترس است',
      transcriptionTimeout: 'زمان رونویسی به پایان رسید',
      transcriptionTimeoutTitle: 'زمان رونویسی به پایان رسید',
      transcriptionTimeoutMessage: 'درخواست رونویسی بیش از حد طول کشید. در حال تلاش مجدد...',
      transcriptionFailed: 'رونویسی ناموفق بود. لطفاً دوباره تلاش کنید.',
      retryingTranscription: 'در حال تلاش مجدد برای رونویسی...',

      // Support Page
      supportAndFeedback: 'پشتیبانی و بازخورد',
      supportHeaderSubtitle: 'ما اینجا هستیم تا به شما کمک کنیم و دوست داریم از شما بشنویم',
      supportThankYou: 'برای استفاده از',
      supportFeedbackInvaluable: 'بازخورد شما برای ما ارزشمند است',
      supportImproveServices: 'ما به بهبود خدمات ترجمه خود ادامه می‌دهیم',
      supportMultilingualCommunication: 'و ارتباط چندزبانه را',
      supportQuestionsSuggestions: 'چه سوال، پیشنهاد،',
      supportTechnicalSupport: 'پشتیبانی فنی،',
      supportShareExperience: 'یا می‌خواهید تجربه خود را با TilHub به اشتراک بگذارید،',
      supportLoveToHear: 'ما دوست داریم از شما بشنویم.',
      supportGetInTouch: 'تماس با ما',
      supportContactUs: 'برای بازخورد، سوالات یا پشتیبانی، لطفاً از طریق آدرس زیر با ما تماس بگیرید:',
      supportSendEmail: 'ارسال ایمیل',
      supportCopyEmailAddress: 'کپی آدرس ایمیل',
      supportEmailCopied: 'آدرس ایمیل به کلیپ‌بورد کپی شد!',
      supportBackToApp: 'بازگشت به برنامه',
      supportDeleteAccount: 'حذف حساب کاربری شما',
      supportDeleteAccountDescription: 'شما می‌توانید در هر زمان درخواست حذف حساب کاربری و تمام داده‌های مرتبط را بدهید.',
      supportRequestAccountDeletion: 'درخواست حذف حساب کاربری',
      supportDeleteAccountConfirmation: 'Are you sure you want to delete your account? This action cannot be undone. All your data will be permanently deleted.',
      supportDeleteAccountFinalConfirm: 'This is your final warning. Your account and all data will be permanently deleted. Are you absolutely sure?',
      supportAccountDeleted: 'Your account has been deleted successfully. You will be logged out now.',
      supportDeleteAccountError: 'Failed to delete account. Please try again or contact support.',
      supportDeletingAccount: 'Deleting...',
      support: 'پشتیبانی',
    },

    // Spanish (Español)
    es: {
      // Navigation
      languages: 'Idiomas',
      languageSettings: 'Configuración de Idioma',
      voiceType: 'Tipo de Voz',
      account: 'Cuenta',
      profile: 'Perfil',
      settings: 'Configuración',
      history: 'Historial',
      wordUsage: 'Uso de Palabras',
      imageUsage: 'Uso de Imágenes',
      analytics: 'Análisis',
      buyWords: 'Comprar Datos de Traducción',
      buyImages: 'Comprar Datos de Imagen',
      buyDataFromWebapp: 'Comprar Datos desde la Aplicación Web',
      login: 'Iniciar Sesión',
      register: 'Registrarse',
      logout: 'Cerrar Sesión',

      // Buy Modals
      buyWordsTitle: 'Comprar Palabras',
      buyWordsSubtitle: 'Comprar datos adicionales para traducción',
      buyImagesTitle: 'Comprar Imágenes',
      buyImagesSubtitle: 'Comprar créditos adicionales de procesamiento de imágenes',
      conversationTopicPlaceholder: 'Tema de conversación (ej: conferencia dental, conferencia médica)',
      conversationTopicHint: 'Opcional: Ayuda a mejorar la precisión de la traducción para temas específicos',
      selectWordPackage: 'Seleccionar Paquete de Palabras',
      selectImagePackage: 'Seleccionar Paquete de Imágenes',
      oneTimePurchase: 'Compra única',
      buyMoreSaveMore: '¡Compra más, ahorra más!',
      popular: 'Popular',
      data: 'datos',
      wordCredits: 'créditos de palabras',
      imageProcessingCredits: 'créditos de procesamiento de imágenes',
      perfectForTesting: 'Perfecto para pruebas',
      save: 'Ahorrar',
      selectAnOption: 'Seleccionar una Opción',
      howItWorks: 'Cómo funciona:',
      oneTimePurchaseAddsWords:
        'La compra única agrega palabras a su cuenta permanentemente',
      wordsConsumedAsYouUse:
        'Las palabras se consumen cuando usa las funciones de traducción',
      noExpirationUseWords: 'Sin expiración - use palabras cuando las necesite',
      oneTimePurchaseAddsImages:
        'La compra única agrega imágenes a su cuenta permanentemente',
      imagesConsumedWhenYouUseOCR:
        'Las imágenes se consumen cuando usa la extracción de texto',
      noExpirationUseImages:
        'Sin expiración - use imágenes cuando las necesite',

      // Language Selection
      yourLanguage: 'Su Idioma:',
      partnerLanguage: 'Idioma de la Pareja:',
      speakingSpeed: 'Velocidad de Habla:',
      slow: 'Lento',
      fast: 'Rápido',

      // Main App
      translation: 'Traducción',
      start: 'Iniciar',
      end: 'Finalizar',
      clear: 'Limpiar',
      clearHistory: 'Limpiar Historial',
      showAll: 'Mostrar Todo',
      readImage: 'Leer Imagen',
      translate: 'Traducir',
      typeMessage: 'Escriba su mensaje aquí...',
      typePartnerMessage: 'Escriba el mensaje de la pareja aquí...',
      maxWords: 'máx 200 palabras',
      words: 'palabras',
      characters: 'caracteres',

      // Conversation
      youSpeakLabel: 'Usted habla',
      partnerSpeaksLabel: 'La pareja habla',

      // History Modal
      conversationHistory: 'Historial de Conversaciones',
      searchConversations: 'Buscar conversaciones...',
      allAudioFiles: 'Todos los Archivos de Audio',
      clearAll: 'Limpiar Todo',
      downloadAll: 'Descargar Todo',
      play: 'Reproducir',
      delete: 'Eliminar',
      confidence: 'Confianza',
      recorded: 'Grabado',
      conversations: 'conversaciones',
      totalDuration: 'duración total',
      noConversationsFound: 'No se encontraron conversaciones',
      noConversationsYet: 'Aún no hay conversaciones',
      tryAdjustingSearch: 'Intente ajustar sus términos de búsqueda',
      startConversationToSee: 'Inicie una conversación para verla aquí',
      youWrote: 'Usted escribió',
      youSpoke: 'Usted habló',
      partnerWrote: 'La pareja escribió',
      partnerHears: 'La pareja escucha',
      noOriginalText: 'Sin texto original',
      noTranslation: 'Sin traducción',
      download: 'Descargar',
      generateAudioFor: 'Generar Audio para',
      generating: 'Generando...',

      // Word Usage Modal
      wordUsageAndQuotas: 'Uso de Palabras y Cuotas',
      trackTranslationUsage: 'Rastree su uso de traducción y gestione las cuotas',
      currentUsage: 'Uso Actual',
      wordsUsage: 'Uso de Palabras',
      switchToTotalView: 'Cambiar a vista Total',
      switchToRemainingView: 'Cambiar a vista Restante',
      of: 'de',
      lastReset: 'Último restablecimiento:',
      quotaSettings: 'Configuración de Cuotas',
      enableWordQuotas: 'Habilitar Cuotas de Palabras',
      setLimitsOnWordUsage: 'Establecer límites en el uso de palabras',
      wordLimit: 'Límite de Palabras',
      maximumWordsPerResetPeriod: 'Palabras máximas por período de restablecimiento',
      resetPeriod: 'Período de Restablecimiento',
      daily: 'Diario',
      weekly: 'Semanal',
      monthly: 'Mensual',
      neverManualResetOnly: 'Nunca (Solo restablecimiento manual)',
      howOftenUsageStatsReset: 'Con qué frecuencia se restablecen las estadísticas de uso',

      // Image Usage Modal
      trackImageProcessingUsage: 'Rastree su uso de procesamiento de imágenes',
      imagesUsage: 'Uso de Imágenes',
      nearLimit: 'Cerca del Límite',
      warning: 'Advertencia',
      good: 'Bueno',
      imagesUsed: 'Imágenes Usadas',
      remaining: 'Restante',
      imagesRemaining: 'imágenes restantes',
      imagesUsedText: 'imágenes usadas',
      imagesAvailable: 'imágenes disponibles',
      statistics: 'Estadísticas',
      totalImagesProcessed: 'Total de Imágenes Procesadas:',
      lastActive: 'Última Actividad:',
      never: 'Nunca',
      accountCreated: 'Cuenta Creada:',
      imageLimitReached: 'Límite de Imágenes Alcanzado',
      imageLimitReachedMessage: 'Ha alcanzado su límite de procesamiento de imágenes. Compre más imágenes para continuar.',
      loadingImageUsageData: 'Cargando datos de uso de imágenes...',

      // Status Messages
      hello: 'Hola',
      processing: 'Procesando...',
      recording: 'Grabando...',
      translating: 'Traduciendo...',
      error: 'Error',
      success: 'Éxito',

      // Success Messages
      wordsAddedSuccessfully: '¡Se agregaron {0} palabras a su cuenta exitosamente!',
      imagesAddedSuccessfully: '¡Se agregaron {0} imágenes a su cuenta exitosamente!',
      textExtractedSuccessfully: '¡Texto extraído de la imagen exitosamente!',
      audioGeneratedSuccessfully: '¡Audio generado exitosamente!',
      originalAudioGeneratedSuccessfully: '¡Audio original generado exitosamente!',
      usageStatsResetSuccessfully: 'Las estadísticas de uso se restablecieron exitosamente.',
      purchaseSuccessful: '¡Compra exitosa!',
      paymentSuccessful: '¡Pago exitoso!',

      // Error Messages
      wordLimitExceeded: 'Límite de palabras excedido',
      wordLimitExceededTitle: 'Límite de Palabras Excedido',
      wordLimitExceededMessage: 'Límite de palabras excedido. Por favor compre más palabras para continuar.',
      wordLimitExceededDetail: '¡Límite de palabras excedido! Ha usado {0} de {1} palabras. Por favor compre más palabras para continuar.',
      textExceedsWordLimit: 'El texto excede el límite de 200 palabras. Por favor acorte su mensaje.',
      pleaseBuyMoreWords: 'Por favor compre más palabras',
      pleaseBuyMoreWordsToContinue: 'Por favor compre más palabras para continuar.',
      purchaseFailed: 'Error en la compra',
      purchaseFailedTryAgain: 'Error en la compra. Por favor intente nuevamente.',
      readImageFailed: 'Error al leer texto de la imagen',
      noTextFoundInImage: 'No se encontró texto en la imagen',
      translationFailed: 'Error en la traducción',
      translationFailedTryAgain: 'Error en la traducción. Por favor intente nuevamente.',
      wordsRemainingWarning: 'Advertencia: Solo le quedan {0} palabras ({1}% usadas). Considere comprar más palabras pronto.',
      approachingWordLimit: 'Acercándose al límite de palabras',

      // Continuous Conversation Modal
      continuousConversation: 'Conversación Continua',
      bidirectionalConversation: 'Conversación Bidireccional',
      readyToTranscribe: 'Listo para transcribir',
      transcribing: 'Transcribiendo...',
      startTranscribing: 'Iniciar Transcripción',
      stopTranscribing: 'Detener Transcripción',
      transcripts: 'Transcripciones',
      conversation: 'Conversación',
      noTranscriptsYet: 'Aún no hay transcripciones. ¡Inicie la transcripción para comenzar!',
      buyMoreWords: 'Comprar Más Palabras',
      dismiss: 'Descartar',
      lectureMode: 'Modo Conferencia',
      lectureModeDescription: 'Grabar cada 20 segundos para mejor contexto de traducción',
      advancedModel: 'Modelo de traducción avanzado',
      advancedModelDescription: 'Usa gpt-5.4-mini. Puede ser más lento.',
      liveTranslation: "Traducción en vivo",
      liveTranslationDescription: "Traduce mientras hablas (gpt-realtime-translate) en Conversación continua y Transmisión. Idiomas de voz: inglés, coreano, mandarín, ruso, kazajo, uzbeko, farsi, español. Voz traducida: inglés, coreano, mandarín, ruso, español.",
      liveTranslationFallback: "La traducción en vivo no admite {0} → {1}. Se usará la traducción estándar.",
      liveTranslationNotSupported: "no disponible en traducción en vivo",

      // Broadcast Translation Modal
      broadcastTranslation: 'Traducción en Vivo',
      speaker: 'Orador',
      listener: 'Oyente',
      sourceLanguage: 'Idioma de Origen',
      sourceLanguageSet: 'El idioma de origen se establece desde su sección',
      targetLanguage: 'Idioma de Destino',
      addLanguage: 'Agregar Idioma',
      broadcastCode: 'Código de Transmisión',
      shareCode: 'Comparte este código con los oyentes',
      startBroadcast: 'Iniciar Transmisión',
      stopBroadcast: 'Detener Transmisión',
      enterCode: 'Ingresar Código de Transmisión',
      selectLanguage: 'Seleccionar Idioma',
      joinBroadcast: 'Unirse a la Transmisión',
      listeningTo: 'Escuchando',
      leaveBroadcast: 'Salir de la Transmisión',
      messages: 'Mensajes',
      waitingForMessages: 'Esperando mensajes...',
      availableLanguages: 'Idiomas disponibles',
      conversationTopic: 'Tema de conversación (opcional)',
      topicPlaceholder: 'Ejemplo: Consulta médica, Reunión de negocios, Planificación de viaje...',
      topicHint: 'Agregar un tema ayuda a mejorar la precisión de la traducción',
      audioOn: 'Audio: ACTIVADO',
      audioOff: 'Audio: DESACTIVADO',
      speaking: 'Hablando...',
      wordCountWarning: 'Nota: Cada idioma adicional multiplicará el conteo de palabras (ej: 2 idiomas = 2x palabras, 3 idiomas = 3x palabras)',

      // Additional error messages
      auth0LoginFailed: 'Error al iniciar sesión con Auth0',
      auth0LoginTimedOut: 'Tiempo de espera agotado para iniciar sesión con Auth0',
      auth0RegistrationFailed: 'Error al registrarse con Auth0',
      auth0RegistrationTimedOut: 'Tiempo de espera agotado para registrarse con Auth0',
      invalidAuthResponse: 'Respuesta de autenticación inválida',
      facebookLoginDenied: 'Inicio de sesión con Facebook denegado',
      facebookPermissionsRequired: 'Se requieren permisos de Facebook',
      loginRequired: 'Se requiere iniciar sesión',
      notMobilePlatform: 'Esta función solo está disponible en plataformas móviles',
      transcriptionTimeout: 'Tiempo de transcripción agotado',
      transcriptionTimeoutTitle: 'Tiempo de Transcripción Agotado',
      transcriptionTimeoutMessage: 'La solicitud de transcripción tardó demasiado. Reintentando...',
      transcriptionFailed: 'La transcripción falló. Por favor, inténtelo de nuevo.',
      retryingTranscription: 'Reintentando transcripción...',

      // Support Page
      supportAndFeedback: 'Soporte y Comentarios',
      supportHeaderSubtitle: 'Estamos aquí para ayudarte y nos encantaría escucharte',
      supportThankYou: 'Gracias por usar',
      supportFeedbackInvaluable: 'Tus comentarios son invaluables para nosotros',
      supportImproveServices: 'mientras continuamos mejorando nuestros servicios de traducción',
      supportMultilingualCommunication: 'y facilitamos la comunicación multilingüe',
      supportQuestionsSuggestions: 'Ya sea que tengas preguntas, sugerencias,',
      supportTechnicalSupport: 'necesites soporte técnico,',
      supportShareExperience: 'o quieras compartir tu experiencia con TilHub,',
      supportLoveToHear: 'nos encantaría escucharte.',
      supportGetInTouch: 'Ponerse en Contacto',
      supportContactUs: 'Para comentarios, preguntas o soporte, por favor contáctanos en:',
      supportSendEmail: 'Enviar Correo',
      supportCopyEmailAddress: 'Copiar Dirección de Correo',
      supportEmailCopied: '¡Dirección de correo copiada al portapapeles!',
      supportBackToApp: 'Volver a la Aplicación',
      supportDeleteAccount: 'Eliminar su cuenta',
      supportDeleteAccountDescription: 'Puede solicitar la eliminación de su cuenta y todos los datos asociados en cualquier momento.',
      supportRequestAccountDeletion: 'Solicitar eliminación de cuenta',
      supportDeleteAccountConfirmation: 'Are you sure you want to delete your account? This action cannot be undone. All your data will be permanently deleted.',
      supportDeleteAccountFinalConfirm: 'This is your final warning. Your account and all data will be permanently deleted. Are you absolutely sure?',
      supportAccountDeleted: 'Your account has been deleted successfully. You will be logged out now.',
      supportDeleteAccountError: 'Failed to delete account. Please try again or contact support.',
      supportDeletingAccount: 'Deleting...',
      support: 'Soporte',
    },
  };

  setLanguage(language: string) {
    console.log('🔄 TranslationService: Setting language to:', language);
    
    // Normalize language codes to match translation keys
    // Map 'zh-CN' and 'zh-HK' to 'zh' since translations use 'zh'
    let normalizedLanguage = language;
    if (language === 'zh-CN' || language === 'zh-HK') {
      normalizedLanguage = 'zh';
      console.log('🔄 TranslationService: Normalized language from', language, 'to', normalizedLanguage);
    }
    
    if (this.translations[normalizedLanguage]) {
      console.log(
        '🔄 TranslationService: Language found, updating current language to:',
        normalizedLanguage
      );
      this.currentLanguage.next(normalizedLanguage);
    } else {
      console.log(
        '🔄 TranslationService: Language not found, available languages:',
        Object.keys(this.translations),
        '- falling back to English'
      );
      // Fallback to English if language not found
      this.currentLanguage.next('en');
    }
  }

  getCurrentLanguage(): string {
    return this.currentLanguage.value;
  }

  getTranslation(key: keyof TranslationKeys): string {
    const currentLang = this.currentLanguage.value;
    const translation = this.translations[currentLang];
    return translation ? translation[key] : this.translations['en'][key];
  }

  getAllTranslations(): TranslationKeys {
    const currentLang = this.currentLanguage.value;
    return this.translations[currentLang] || this.translations['en'];
  }

  // Format message with placeholders like {0}, {1}, etc.
  formatMessage(key: keyof TranslationKeys, ...args: (string | number)[]): string {
    let message = this.getTranslation(key);
    
    // Replace placeholders {0}, {1}, etc. with arguments
    args.forEach((arg, index) => {
      message = message.replace(`{${index}}`, String(arg));
    });
    
    return message;
  }
}
